import React, { useCallback, useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import ChatMarkdown from "./ChatMarkdown";
import Visuals from "./ChatVisualsLazy";
import { answerActions, answerSource, isInvestigation, splitFollowUps, statusText } from "./chatAnswer";
import { streamChat } from "./chatStream";
import { requestDashboard } from "./assistantStore";
import { contextLabel, contextRows, mergePageContext, pageQuestions, usePageParts } from "./pageContext";
import { commandSuggestions, navigationDetails, navigationTarget } from "./chatNavigation";
import { answerDatasets, answerDocument, exportAnswer } from "./chatExport";
import { pageAt } from "../mw2/context";
import { useDistrict } from "../../utils/DistrictContext";

// MizuIQ's window - the MizuWatch AI assistant. It opens as the design's floating
// window, above its button, and can be:
//   - dragged anywhere in the browser window, by its title bar, the footer
//     around the message box or the grip at its foot;
//   - resized from any edge or corner;
//   - pinned to the right: a full-height panel, widened or narrowed from its
//     left edge, with the app laid out in the space left of it - the
//     <html> class mizu-chat-docked and --mizu-chat-dock (its width) narrow
//     #root, and the pages' fixed parts keep to that space (the dashboard's
//     in newDashboard.overrides.css). Dragged by its title bar, it floats
//     again;
//   - expanded to full screen - Esc, or the same button, brings it back.
// Its size, spot and pinning are remembered (localStorage); full screen is
// not, and closing leaves it.
//
// An answer (Answer) is the assistant's text - its headline figures as tiles
// (ChatMarkdown) - then buttons for what it is about ("Locate on map" rings
// its meters on the New Dashboard's map; "Open ..." the page with more;
// "Investigate root cause" asks why), the source of its data, and under the
// latest one the follow-up questions it suggests (chatAnswer.js).
//
// Investigation mode - the Investigate switch under the message box: each
// question is answered with a root cause analysis (the API's mode
// "investigate"): headed "Root cause analysis", its likely causes ranked by
// the evidence for them, the evidence, what is ruled out and what to check.
//
// Super Mode - the Super switch beside it, for the accounts it is open to
// (the API's "features"): each question is answered with one read-only SQL
// query (the API's mode "super", python agentic_ai/super_mode.py), checked
// before it is shown - the query, what it reads in plain words, and Run.
// Nothing runs until the user runs it (run_query: its id); then its rows
// come as a table - tiles, or a chart, when they make one - and an answer
// written from them.
//
// Pages - asked for in words ("take me to Leak Flow for meter 126171") or
// with a command (/open <page>, /pages, /back, /help - suggested as "/" is
// typed), an answer opens the page (python agentic_ai/navigation.py,
// chatNavigation.js), with the meter, period or district asked for, and a
// card to open it again; one that is not sure which page offers them.
//
// Page context - the chip beside it: each question is sent with what the
// user is looking at (pageContext.js) - the page and the part of it in view,
// the district, its filters, the meter in focus, the meters on screen - so
// that "this meter" or "these leaks" are the ones on the screen. Its details
// show what is sent, and it can be turned off (remembered). A question
// asked with it is tagged with its page.
//
// `open` / `onOpenChange`: opened and closed from outside, by a page with a
// button of its own for it (the New Dashboard's robot, in its map corner),
// which passes `showButton={false}` to leave out the floating one. Left out,
// the widget opens and closes itself, from its own button.

const LAYOUT_KEY = "mizuwatch.chatbot.layout.v1";
const PAGE_CONTEXT_KEY = "mizuwatch.chatbot.pageContext.v1";
const SIZE = { width: 400, height: 600 }; // the design's window
const MIN_SIZE = { width: 300, height: 360 };
const EDGE_GAP = 8; // a floating window is held this far inside the browser window
// Pinned: its width at first and at the least; at the most, half the window.
const DOCK = { width: 400, min: 320, maxShare: 0.5 };
// What pinning leaves the app at the least - no pinning in a window too
// narrow for that beside the narrowest panel.
const MIN_APP_WIDTH = 640;
const DRAG_THRESHOLD = 4; // how far a press travels before it is a drag
const RESIZE_EDGES = ["n", "e", "s", "w", "ne", "se", "sw", "nw"];
const CURSORS = {
  move: "grabbing",
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
  nw: "nwse-resize",
  se: "nwse-resize",
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const viewportSize = () => ({ width: window.innerWidth, height: window.innerHeight });
const canPin = (view) => view.width - DOCK.min >= MIN_APP_WIDTH;
const maxDockWidth = (view) => Math.max(DOCK.min, Math.min(view.width * DOCK.maxShare, view.width - MIN_APP_WIDTH));

function loadLayout() {
  const fallback = { rect: null, docked: false, dockWidth: DOCK.width };
  try {
    const saved = JSON.parse(window.localStorage.getItem(LAYOUT_KEY) || "null");
    if (!saved || typeof saved !== "object") return fallback;
    const rect =
      saved.rect && ["left", "top", "width", "height"].every((key) => Number.isFinite(saved.rect[key]))
        ? saved.rect
        : null;
    return {
      rect,
      docked: saved.docked === true,
      dockWidth: Number.isFinite(saved.dockWidth) ? saved.dockWidth : DOCK.width,
    };
  } catch {
    return fallback;
  }
}

function saveLayout(layout) {
  try {
    window.localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
  } catch {
    // Storage blocked (a private window, site data off): kept for this visit.
  }
}

// A floating window's box held inside the browser window: no smaller than
// MIN_SIZE (unless the window is), no bigger than the window, all of it on
// screen.
function fitRect(rect, view) {
  const maxWidth = Math.max(0, view.width - 2 * EDGE_GAP);
  const maxHeight = Math.max(0, view.height - 2 * EDGE_GAP);
  const width = clamp(rect.width, Math.min(MIN_SIZE.width, maxWidth), maxWidth);
  const height = clamp(rect.height, Math.min(MIN_SIZE.height, maxHeight), maxHeight);
  return {
    left: clamp(rect.left, EDGE_GAP, view.width - EDGE_GAP - width),
    top: clamp(rect.top, EDGE_GAP, view.height - EDGE_GAP - height),
    width,
    height,
  };
}

// Where the window opens until it is moved: the design's spot, 72px above
// its button and 16px inside the browser window.
function besideButton(button, view) {
  const right = clamp(button.right, 16, Math.max(16, view.width - SIZE.width - 16));
  const bottom = Math.min(button.bottom + 72, Math.max(16, view.height - SIZE.height - 16));
  return { left: view.width - right - SIZE.width, top: view.height - bottom - SIZE.height, ...SIZE };
}

// A floating window pulled by one edge or corner, (dx, dy) from where the
// pull started - the opposite edges staying put.
function resizeRect(start, edge, dx, dy, view) {
  const minWidth = Math.min(MIN_SIZE.width, view.width - 2 * EDGE_GAP);
  const minHeight = Math.min(MIN_SIZE.height, view.height - 2 * EDGE_GAP);
  let { left, top, width, height } = start;
  if (edge.includes("e")) width = clamp(start.width + dx, minWidth, view.width - EDGE_GAP - start.left);
  if (edge.includes("s")) height = clamp(start.height + dy, minHeight, view.height - EDGE_GAP - start.top);
  if (edge.includes("w")) {
    const right = start.left + start.width;
    left = clamp(start.left + dx, EDGE_GAP, right - minWidth);
    width = right - left;
  }
  if (edge.includes("n")) {
    const bottom = start.top + start.height;
    top = clamp(start.top + dy, EDGE_GAP, bottom - minHeight);
    height = bottom - top;
  }
  return { left, top, width, height };
}

// The app's width changed while the browser window's did not: charts and
// maps that measure again on the window's resize event are told - once a
// frame, however often it changes.
let resizeFrame = 0;
function announceResize() {
  if (resizeFrame) return;
  resizeFrame = window.requestAnimationFrame(() => {
    resizeFrame = 0;
    window.dispatchEvent(new Event("resize"));
  });
}

// Questions to start a conversation from, each one the assistant has a tool
// to answer (python/flask_api/agentic_ai/tools/schemas.py).
const SAMPLE_QUESTIONS = [
  "Which meters are leaking right now?",
  "What was last week's water accountability?",
  "How much water did we lose last month?",
  "How many anomalies in the last 7 days?",
];
// And in Investigation mode, issues to find the causes of (investigateIssue).
const INVESTIGATION_QUESTIONS = [
  "What is causing the active leaks?",
  "What is behind the recent anomalies?",
  "Why are meters going silent?",
  "What is affecting accountability this month?",
];
// And in Super Mode, questions a query answers (agentic_ai/super_mode.py).
const SUPER_QUESTIONS = [
  "Top 10 meters by water use in the last 30 days",
  "Daily total flow for the past two weeks",
  "How many leaks were reported per category this year?",
  "Which meters have the most anomaly days this month?",
];

// Each page's name, as its heading or the sidebar has it - the page context
// starts from it, and a page's own (usePageContext) can say more.
const PAGE_TITLES = {
  "/new-dashboard": "Dashboard",
  "/dashboard": "Dashboard",
  "/waters": "Water Usage",
  "/watersold": "Water Usage",
  "/waterscombined": "Water Usage",
  "/totalflow": "Analytics",
  "/capacity-planning": "Capacity Planning",
  "/schematics": "Digital Twin",
  "/reportsandalerts": "Reports & Alerts",
  "/alert": "Alerts",
  "/outliersnew": "Data Outliers",
  "/health": "System Health",
  "/flow-health": "Flow Health",
  "/leak-flow-category": "Leak Flow",
  "/meter-leak-details": "Meter Details",
  "/drought-dashboard": "Drought Control Dashboard",
  "/exemption": "Drought Exemptions",
  "/rule-configuration": "Drought Rules",
  "/violation-details": "Violation Details",
  "/drought-report": "Drought Report",
  "/settingsuser": "Administration",
  "/profile": "Profile",
};

const storedData = () => {
  try {
    return JSON.parse(localStorage.getItem("MCData") || "null") || {};
  } catch {
    return {};
  }
};

// The district selected, by its name - "Grand Prairie (GPMD)" - for the
// page context; the data comes from it whatever the context (the District
// header).
function districtLabel(code) {
  if (!code) return null;
  const list = storedData().district;
  const name = Array.isArray(list) ? list.find((item) => item?.district_name === code)?.district_display_name : null;
  return name && name !== code ? `${name} (${code})` : code;
}

function loadContextOn() {
  try {
    return window.localStorage.getItem(PAGE_CONTEXT_KEY) !== "off";
  } catch {
    return true;
  }
}

// The chatbot API's address, the signed-in user's token and the district
// they have selected - which the answers' data comes from, as the app's
// other calls send it (services/api.jsx).
function chatbotEndpoint(path = "") {
  const data = storedData();
  const isLocalDev = import.meta.env.DEV;
  const baseUrl = isLocalDev ? "http://localhost:5000" : (import.meta.env.VITE_API_URL || "");
  return {
    url: `${baseUrl.replace(/\/$/, "")}/apis/chatbot${path}`,
    headers: { Authorization: `Bearer ${data.token || ""}`, District: data.selectedDistrict || "" },
  };
}

// The chatbot API, with the signed-in user's token: a conversation (GET
// apis/chatbot - the most recent, or ?conversation_id=) and the user's
// conversations (GET apis/chatbot/conversations). A question (POST
// apis/chatbot) is asked with its answer streamed (chatStream.js).
async function chatbotApi(method, path = "", { params, data } = {}) {
  const axios = (await import("axios")).default;
  const response = await axios({ method, ...chatbotEndpoint(path), params, data });
  return response.data;
}

function signedIn() {
  try {
    return !!JSON.parse(localStorage.getItem("MCData") || "null")?.token;
  } catch {
    return false;
  }
}

// What heads a root cause analysis ({ issue, title, period, sources,
// alert_type }), or null.
const investigationOf = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : null);

// The figures, charts and tables an answer is shown with (ChatVisuals).
const visualsOf = (value) => (Array.isArray(value) ? value.filter((visual) => visual && typeof visual === "object") : []);

// The page a question was asked on (pageContext.js), or undefined.
const contextOf = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : undefined);

// A Super Mode answer's query - { id, kind: "proposal" | "result", status,
// sql, title, ... } (python agentic_ai/super_mode.py) - or null.
const sqlOf = (value) =>
  value && typeof value === "object" && typeof value.sql === "string" && (value.kind === "proposal" || value.kind === "result") ? value : null;

// A question's mode, as the window marks it.
const modeOf = (value) => (value === "investigate" || value === "super" ? value : undefined);

// The page an answer opened - { page, label, params } or { back: true } - or
// the pages it offers ({ choices }) (chatNavigation.js), or null.
const navigationOf = (value) =>
  value && typeof value === "object" && (typeof value.page === "string" || value.back === true || Array.isArray(value.choices))
    ? value
    : null;

// A conversation's questions and answers as the window shows them - an
// answer with the data tools it called and the meters it is about, for its
// Source line and buttons, what heads it when it is an investigation, its
// figures, charts and tables, its query in Super Mode and the page it
// opened; a question asked in Investigation mode or Super Mode with that
// mode (and the query it approved), and one asked with its page in view
// with that page. An answer to a Super Mode question is one too (its
// follow-ups are asked in it).
const toMessages = (list) => {
  let asked;
  return (Array.isArray(list) ? list : [])
    .filter((msg) => (msg.role === "user" || msg.role === "assistant") && msg.content)
    .map((msg) => {
      if (msg.role === "user") asked = modeOf(msg.mode);
      return {
        role: msg.role,
        content: msg.content,
        at: msg.created_at || null,
        tools: Array.isArray(msg.tools) ? msg.tools : [],
        meterIds: Array.isArray(msg.meter_ids) ? msg.meter_ids : [],
        investigation: investigationOf(msg.investigation),
        visuals: visualsOf(msg.visuals),
        sql: msg.role === "assistant" ? sqlOf(msg.sql) : null,
        navigation: msg.role === "assistant" ? navigationOf(msg.navigation) : null,
        district: msg.role === "assistant" && typeof msg.district === "string" ? msg.district : null,
        superMode: msg.role === "assistant" && asked === "super",
        mode: msg.role === "user" ? modeOf(msg.mode) : undefined,
        runQuery: msg.role === "user" && typeof msg.run_query === "string" ? msg.run_query : undefined,
        context: msg.role === "user" ? contextOf(msg.context) : undefined,
      };
    });
};

const dateOf = (at) => {
  const date = at ? new Date(at) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

// A message's time - with its date, from another day.
function timeOf(at) {
  const date = dateOf(at);
  if (!date) return "";
  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (date.toDateString() === new Date().toDateString()) return time;
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
}

// When a conversation was last added to: the time today, then Yesterday, the
// weekday that week, the date before.
function whenOf(at) {
  const date = dateOf(at);
  if (!date) return "";
  const today = new Date();
  const days = Math.round((new Date(today.toDateString()) - new Date(date.toDateString())) / 86400000);
  if (days <= 0) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (days === 1) return "Yesterday";
  if (days < 7) return date.toLocaleDateString([], { weekday: "long" });
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}

function BrainIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      {/* Left Brain Hemisphere */}
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-2.5 2.5C6 22 3 19 3 12S6 2 9.5 2z" />
      {/* Right Hemisphere as neural nodes/circuits */}
      <path d="M12 4.5a2.5 2.5 0 0 1 2.5-2.5C18 2 21 5 21 12s-3 10-6.5 10a2.5 2.5 0 0 1-2.5-2.5" strokeDasharray="2 2" />
      <circle cx="16" cy="7" r="1.2" fill="currentColor" />
      <circle cx="18" cy="12" r="1.2" fill="currentColor" />
      <circle cx="15" cy="17" r="1.2" fill="currentColor" />
      <path d="M12 7h2.5M12 12h4.5M12 17h1.5" />
    </svg>
  );
}

// Line icons, 24-unit, drawn in the text's colour.
function Icon({ children, size, ...props }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

// The assistant's mark: a four-point sparkle.
const SparkleIcon = (props) => (
  <Icon {...props}>
    <path d="M9.94 15.5a2 2 0 0 0-1.44-1.44l-6.13-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.13a.5.5 0 0 1 .96 0L14.06 8.5a2 2 0 0 0 1.44 1.44l6.13 1.58a.5.5 0 0 1 0 .96L15.5 14.06a2 2 0 0 0-1.44 1.44l-1.58 6.13a.5.5 0 0 1-.96 0z" />
  </Icon>
);
const PinIcon = (props) => (
  <Icon {...props}>
    <path d="M20 10c0 4.99-5.54 10.19-7.4 11.8a1 1 0 0 1-1.2 0C9.54 20.19 4 14.99 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </Icon>
);
const ArrowIcon = (props) => (
  <Icon {...props}>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </Icon>
);
const InfoIcon = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </Icon>
);
const SendIcon = (props) => (
  <Icon {...props}>
    <path d="M14.54 21.69a.5.5 0 0 0 .94-.03l6.5-19a.5.5 0 0 0-.64-.63l-19 6.5a.5.5 0 0 0-.02.93l7.93 3.18a2 2 0 0 1 1.11 1.11z" />
    <path d="m21.85 2.15-10.94 10.94" />
  </Icon>
);
// Investigation mode's mark: a magnifier.
const SearchIcon = (props) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.6-3.6" />
  </Icon>
);
// Super Mode's: a database.
const DatabaseIcon = (props) => (
  <Icon {...props}>
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v14c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
    <path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3" />
  </Icon>
);
const PlayIcon = (props) => (
  <Icon {...props}>
    <path d="M7 4.5v15a.5.5 0 0 0 .76.43l12-7.5a.5.5 0 0 0 0-.86l-12-7.5A.5.5 0 0 0 7 4.5z" />
  </Icon>
);
const CheckIcon = (props) => (
  <Icon {...props}>
    <path d="M20 6 9 17l-5-5" />
  </Icon>
);
// A page opened: an arrow out of a window.
const OpenIcon = (props) => (
  <Icon {...props}>
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </Icon>
);

// The page context: what the assistant sees.
const EyeIcon = (props) => (
  <Icon {...props}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

// An answer exported (ExportMenu).
const DownloadIcon = (props) => (
  <Icon {...props}>
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </Icon>
);

// The page context by the message box: what the questions are sent with, in
// a few words - on a click, all of it, and the switch for it. Closed by
// another click, a click elsewhere or Esc.
function PageContextChip({ context, on, open, onOpenChange, onToggle }) {
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!wrapRef.current?.contains(event.target)) onOpenChange(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onOpenChange(false);
      }
    };
    document.addEventListener("pointerdown", onPointer);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open, onOpenChange]);
  const label = contextLabel(context);
  const rows = contextRows(context);
  return (
    <div className="mizu-chat-context" ref={wrapRef}>
      <button
        type="button"
        className={`mizu-chat-context-chip${on ? "" : " is-off"}`}
        aria-expanded={open}
        aria-controls="mizu-chat-context-panel"
        onClick={() => onOpenChange(!open)}
        title={on ? `Questions are asked about what you're viewing: ${label}` : "Page context is off"}
      >
        <EyeIcon size={13} />
        <span className="mizu-chat-context-label">{on ? label : "Page context off"}</span>
      </button>
      {open ? (
        <div className="mizu-chat-context-panel" id="mizu-chat-context-panel" role="dialog" aria-label="Page context">
          <div className="mizu-chat-context-head">
            <span>Page context</span>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label="Send the page with each question"
              className={`mizu-chat-switch${on ? " on" : ""}`}
              onClick={onToggle}
            >
              <span className="mizu-chat-switch-knob" />
            </button>
          </div>
          <p className="mizu-chat-context-note">
            {on
              ? "Sent with each question, so “this meter” or “these leaks” mean the ones on your screen, and its filters are used when you don't name any."
              : "Off: questions are answered without this page. Turn it on to ask about what you're viewing."}
          </p>
          {on && rows.length ? (
            <dl className="mizu-chat-context-rows">
              {rows.map(([name, value]) => (
                <React.Fragment key={name}>
                  <dt>{name}</dt>
                  <dd>{value}</dd>
                </React.Fragment>
              ))}
            </dl>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// A new conversation: what the assistant answers, and questions to start
// from - each asked with a click (not while an answer is still coming) - the
// first about the page in view, when its context is sent. In Investigation
// mode, what an investigation looks at, and issues to start on; in Super
// Mode, how a question is answered, and questions a query answers.
function Welcome({ onAsk, disabled, investigating, superMode, context }) {
  const samples = superMode ? SUPER_QUESTIONS : investigating ? INVESTIGATION_QUESTIONS : SAMPLE_QUESTIONS;
  // The page's own questions are the data tools': not Super Mode's.
  const questions = superMode ? samples : [...pageQuestions(context, investigating), ...samples].slice(0, samples.length);
  return (
    <div className={`mizu-chat-welcome${superMode ? " is-super" : investigating ? " is-investigating" : ""}`}>
      <div className="mizu-chat-welcome-icon" aria-hidden="true">
        {superMode ? <DatabaseIcon /> : investigating ? <SearchIcon /> : <SparkleIcon />}
      </div>
      <div className="mizu-chat-welcome-title">{superMode ? "Super Mode" : investigating ? "Investigation mode" : "MizuIQ"}</div>
      <p className="mizu-chat-welcome-text">
        {superMode
          ? "Ask anything about your district's data. MizuIQ writes one read-only SQL query for it and checks it against the database — it runs only when you approve it."
          : investigating
            ? "Ask why something is happening. Each answer checks leak records, daily use, the anomaly model, meter health, nearby meters and maintenance logs, and ranks the likely causes by the evidence."
            : "Ask about water accountability, leaks, water loss, anomalies and meter health — answered from your MizuWatch data."}
      </p>
      <div
        className="mizu-chat-welcome-samples"
        role="group"
        aria-label={superMode ? "Questions to answer with a query" : investigating ? "Issues to investigate" : "Sample questions"}
      >
        {questions.map((question) => (
          <button
            key={question}
            type="button"
            className="mizu-chat-sample"
            disabled={disabled}
            onClick={() => onAsk(question)}
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  );
}

// The head of each answer: the assistant - and, on hover, when it answered
// - with its tools (children) at the end.
function AssistantFrom({ at, children = null }) {
  const time = timeOf(at);
  return (
    <div className={`mizu-msg-from${children ? " has-tools" : ""}`} title={time ? `Answered ${time}` : undefined}>
      <span className="mizu-msg-avatar" aria-hidden="true">
        <SparkleIcon />
      </span>
      <span className="mizu-msg-name">MizuIQ</span>
      {children}
    </div>
  );
}

// An answer's Export menu: the answer and its data as an Excel workbook or a
// PDF, or as CSV - a file a dataset, or the question and answer as one row
// when it has none (chatExport.js). Closed by a pick, a click elsewhere or
// Esc.
function ExportMenu({ message, question }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  // Read when the menu opens, not with every answer drawn.
  const doc = useMemo(() => (open ? answerDocument(message, question) : null), [open, message, question]);
  const datasets = useMemo(() => (doc ? answerDatasets(doc) : []), [doc]);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointer);
    window.addEventListener("keydown", onKey, true);
    rootRef.current?.querySelector(".mizu-export-item")?.focus();
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);
  const run = async (format, index = null) => {
    setOpen(false);
    setBusy(format);
    setFailed(null);
    try {
      await exportAnswer(doc, format, index);
    } catch (err) {
      console.error("Failed to export the answer:", err);
      setFailed("Couldn't export this answer - try again.");
    } finally {
      setBusy(null);
    }
  };
  // Up and Down move between the choices.
  const onMenuKey = (event) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = [...rootRef.current.querySelectorAll(".mizu-export-item")];
    const at = items.indexOf(document.activeElement);
    items[(at + (event.key === "ArrowDown" ? 1 : items.length - 1)) % items.length]?.focus();
  };
  const rowsText = (count) => `${count.toLocaleString()} ${count === 1 ? "row" : "rows"}`;
  return (
    <div className="mizu-export" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="mizu-export-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={!!busy}
        onClick={() => setOpen((value) => !value)}
        title="Export this answer as Excel, CSV or PDF"
      >
        <DownloadIcon size={13} />
        {busy ? "Exporting…" : "Export"}
      </button>
      {failed ? (
        <span className="mizu-export-failed" role="alert">
          {failed}
        </span>
      ) : null}
      {open && doc ? (
        <div className="mizu-export-menu" role="menu" aria-label="Export this answer" onKeyDown={onMenuKey}>
          <button type="button" role="menuitem" className="mizu-export-item" onClick={() => run("xlsx")}>
            <span>Excel workbook</span>
            <small>{datasets.length ? `The answer and ${datasets.length} ${datasets.length === 1 ? "data sheet" : "data sheets"}` : "The answer"}</small>
          </button>
          <button type="button" role="menuitem" className="mizu-export-item" onClick={() => run("pdf")}>
            <span>PDF document</span>
            <small>Its text, figures, charts and tables</small>
          </button>
          {datasets.length ? (
            datasets.map((dataset, index) => (
              <button key={dataset.name} type="button" role="menuitem" className="mizu-export-item" onClick={() => run("csv", index)}>
                <span>CSV · {dataset.name}</span>
                <small>{rowsText(dataset.rows.length)}</small>
              </button>
            ))
          ) : (
            <button type="button" role="menuitem" className="mizu-export-item" onClick={() => run("csv")}>
              <span>CSV</span>
              <small>The question and answer, as one row</small>
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

// A deep link's context in words, on its button - the district only in its
// hover text: "meter 126171 · past week".
const linkContext = (navigation) =>
  navigationDetails({ ...navigation, params: { ...navigation.params, district: undefined } }).join(" · ");

// An answer's button's hover text.
function actionTitle(action) {
  if (action.kind === "locate") return `Show ${action.meterIds.length.toLocaleString()} ${action.setLabel} on the dashboard's map`;
  if (action.kind === "investigate") return `Ask "${action.question}" in Investigation mode`;
  if (action.kind === "link") {
    const details = navigationDetails(action.navigation);
    return `Open ${action.navigation.label}${details.length ? ` with ${details.join(" · ")}` : ""}`;
  }
  return undefined;
}

// An answer on its way, before its text: what it is doing (the stream's
// status) - its figures and charts may already be there.
function Progress({ status, investigating, superMode }) {
  return (
    <div className="mizu-progress" role="status" aria-live="polite" aria-label={investigating ? "Investigating" : "Answering"}>
      <div className="mizu-typing-dots" aria-hidden="true">
        <div className="mizu-dot"></div>
        <div className="mizu-dot"></div>
        <div className="mizu-dot"></div>
      </div>
      <span className="mizu-typing-note">{statusText(status, investigating, superMode)}</span>
    </div>
  );
}

// A query as the card shows it: on several lines - one written on a single
// line is broken before its clauses (outside its strings, names and
// brackets) - with its keywords, strings, numbers and comments marked.
const SQL_CLAUSE = /^(from|where|group|order|having|limit|offset|union|intersect|except|window|join|left|right|inner|full|cross)$/i;
const SQL_KEYWORDS = new Set(
  ("select from where and or not in is null as on join left right inner outer full cross lateral group by order having " +
    "limit offset with union intersect except all distinct case when then else end between like ilike asc desc nulls over " +
    "partition filter interval exists any true false cast using recursive materialized fetch first rows only within at time zone")
    .split(" "),
);
const SQL_TOKEN = /('(?:[^']|'')*'?)|("(?:[^"]|"")*"?)|(--[^\n]*)|(\b\d+(?:\.\d+)?\b)|([A-Za-z_][A-Za-z0-9_]*)|([()])/g;

function layoutSql(sql) {
  if (sql.includes("\n")) return sql;
  let depth = 0;
  let out = "";
  let last = 0;
  let previous = "";
  for (const match of sql.matchAll(SQL_TOKEN)) {
    const [text, , , , , word, bracket] = match;
    if (bracket) depth += bracket === "(" ? 1 : -1;
    // "LEFT JOIN", "GROUP BY": broken before the first word only - and not
    // before left(name, 3), a function.
    const called = word && /^\s*\(/.test(sql.slice(match.index + text.length));
    if (word && depth === 0 && !called && SQL_CLAUSE.test(word) && !(/^join$/i.test(word) && /^(left|right|inner|full|cross|outer)$/i.test(previous))) {
      out += `${sql.slice(last, match.index).replace(/\s+$/, "")}\n`;
      last = match.index;
    }
    if (word) previous = word;
    else if (!/^\s*$/.test(text)) previous = "";
  }
  return out + sql.slice(last);
}

function highlightSql(sql) {
  const out = [];
  let last = 0;
  for (const match of sql.matchAll(SQL_TOKEN)) {
    const [text, string, , comment, number, word] = match;
    const kind = string ? "s" : comment ? "c" : number ? "n" : word && SQL_KEYWORDS.has(word.toLowerCase()) ? "k" : null;
    if (!kind) continue;
    if (match.index > last) out.push(sql.slice(last, match.index));
    out.push(
      <span key={match.index} className={`mizu-sql-${kind}`}>
        {text}
      </span>,
    );
    last = match.index + text.length;
  }
  if (last < sql.length) out.push(sql.slice(last));
  return out;
}

const rowsText = (count, truncated) =>
  `${Number(count || 0).toLocaleString()}${truncated ? "+" : ""} ${Number(count) === 1 && !truncated ? "row" : "rows"}`;

// A Super Mode answer's query. Proposed: what its result is, the SQL, what
// was checked, its limits, and Run - Run again once it has run - for an
// account Super Mode is open to. Refused: the last one written, not run. A
// result, or a run that stopped: a line - what ran, its rows and time - and
// its SQL on a click.
function SqlCard({ sql, canRun, busy, onRun }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = useMemo(() => layoutSql(sql.sql), [sql.sql]);
  const code = useMemo(() => highlightSql(text), [text]);
  const canCopy = typeof navigator !== "undefined" && !!navigator.clipboard;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Not allowed here (an insecure page, or permission refused).
    }
  };
  const copyButton = canCopy ? (
    <button type="button" className="mizu-md-copy" onClick={copy}>
      {copied ? "Copied" : "Copy"}
    </button>
  ) : null;

  if (sql.kind === "result") {
    const failed = sql.status === "failed";
    const facts = failed
      ? ["Stopped — no results"]
      : [
          rowsText(sql.row_count, sql.truncated),
          Number.isFinite(sql.ms) ? (sql.ms < 1000 ? `${sql.ms} ms` : `${(sql.ms / 1000).toFixed(sql.ms < 10000 ? 1 : 0)} s`) : null,
        ];
    return (
      <div className={`mizu-sql is-result${failed ? " is-failed" : ""}`}>
        <div className="mizu-sql-line">
          <DatabaseIcon size={13} />
          <span className="mizu-sql-line-text" title={sql.title}>
            {[...facts, sql.district].filter(Boolean).join(" · ")}
          </span>
          <button type="button" className="mizu-sql-toggle" aria-expanded={open} onClick={() => setOpen((on) => !on)}>
            {open ? "Hide SQL" : "Show SQL"}
          </button>
        </div>
        {open ? (
          <div className="mizu-sql-box">
            <div className="mizu-sql-box-head">
              <span>{sql.title}</span>
              {copyButton}
            </div>
            <pre className="mizu-sql-code">
              <code>{code}</code>
            </pre>
          </div>
        ) : null}
      </div>
    );
  }

  const rejected = sql.status === "rejected";
  const ran = sql.status === "ran";
  const stopped = sql.status === "failed";
  const relations = Array.isArray(sql.relations) ? sql.relations : [];
  const state = ran
    ? `Ran ${timeOf(sql.ran_at)} · ${rowsText(sql.row_count)}`
    : stopped
      ? `Stopped ${timeOf(sql.ran_at)}`
      : [Number.isFinite(sql.max_rows) ? `Up to ${sql.max_rows.toLocaleString()} rows` : null, Number.isFinite(sql.timeout_s) ? `${sql.timeout_s} s limit` : null, sql.district]
          .filter(Boolean)
          .join(" · ");
  return (
    <div className={`mizu-sql${rejected ? " is-rejected" : ""}`}>
      <div className="mizu-sql-head">
        <span className="mizu-sql-kicker">
          <DatabaseIcon size={13} />
          {rejected ? "Not run" : "SQL query · read-only"}
        </span>
        {copyButton}
      </div>
      <div className="mizu-sql-title">{sql.title}</div>
      <pre className="mizu-sql-code">
        <code>{code}</code>
      </pre>
      {rejected ? (
        <div className="mizu-sql-checks is-refused">Refused by Super Mode&apos;s checks — the reasons are above.</div>
      ) : (
        <>
          <div className="mizu-sql-checks">
            <CheckIcon size={13} />
            <span>
              Checked: one read-only SELECT{relations.length ? ` · reads ${relations.join(", ")}` : ""}
            </span>
          </div>
          <div className="mizu-sql-foot">
            <button
              type="button"
              className="mizu-sql-run"
              disabled={!canRun || busy}
              onClick={() => onRun(sql)}
              title={canRun ? `Run it on ${sql.district || "the district"}'s data, read-only` : "Super Mode isn't available for your account"}
            >
              <PlayIcon size={13} />
              {ran || stopped ? "Run again" : "Run query"}
            </button>
            <span className="mizu-sql-state">{state}</span>
          </div>
        </>
      )}
    </div>
  );
}

// The page an answer opened: its name, the context it was opened with, and
// Open - "Open again" once it has opened (an answer read back from History
// opens nothing by itself). Pages to pick from, as buttons. `note`: why a
// page could not be opened.
function PageCard({ navigation, opened, note, onOpen }) {
  if (navigation.choices) {
    return (
      <div className="mizu-nav-choices" role="group" aria-label="Pages">
        {navigation.choices
          .filter((choice) => choice && typeof choice.page === "string")
          .map((choice) => (
            <button key={choice.page} type="button" className="mizu-nav-choice" onClick={() => onOpen({ page: choice.page, label: choice.label, params: {} })}>
              <span>{choice.label}</span>
              {choice.group ? <small>{choice.group}</small> : null}
            </button>
          ))}
      </div>
    );
  }
  if (navigation.back) return note ? <div className="mizu-nav-note">{note}</div> : null;
  if (!navigationTarget(navigation)) return null;
  const details = navigationDetails(navigation);
  return (
    <div className="mizu-nav">
      <span className="mizu-nav-icon" aria-hidden="true">
        <OpenIcon size={15} />
      </span>
      <div className="mizu-nav-text">
        <div className="mizu-nav-label">{navigation.label}</div>
        {details.length ? <div className="mizu-nav-details">{details.join(" · ")}</div> : null}
        {note ? <div className="mizu-nav-note">{note}</div> : null}
      </div>
      <button type="button" className="mizu-nav-open" onClick={() => onOpen(navigation)} title={`Open ${navigation.label}`}>
        {opened ? "Open again" : "Open"}
      </button>
    </div>
  );
}

// An answer: its text - headed "Root cause analysis" when it is one, its
// figures, charts and tables after its opening sentence - its query in
// Super Mode (SqlCard), the page it opened (PageCard), then the buttons for
// what it is about, where its data came from, and - under the latest answer
// - the follow-up questions it suggests, each asked with a click (an
// investigation's in Investigation mode, a Super Mode answer's in Super
// Mode while it is open to the user). On its way (streaming): what it is
// doing until its text comes, its visuals as soon as they are in, its text
// as it is written - its buttons once it is complete.
function Answer({ message, question, latest, onAsk, onAction, onVisuals, onRunQuery, onOpenPage, canSuper, busy }) {
  const { body, followUps } = useMemo(() => splitFollowUps(message.content), [message.content]);
  const actions = useMemo(
    () => answerActions(message.tools, message.meterIds, message.investigation, message.district),
    [message.tools, message.meterIds, message.investigation, message.district],
  );
  const source = useMemo(() => answerSource(message.tools, message.investigation), [message.tools, message.investigation]);
  const investigation = message.investigation;
  const streaming = !!message.streaming;
  const visuals = message.visuals || [];
  // Tiles of the server's: the answer's own ```stats would repeat them.
  const tiled = visuals.some((visual) => visual.kind === "kpis");
  const done = !streaming;
  const sql = message.sql;
  const superAnswer = !!sql || !!message.superMode;
  const followMode = (question) => (superAnswer && canSuper ? "super" : isInvestigation(question) ? "investigate" : undefined);
  return (
    <div className={`mizu-msg assistant${streaming ? " is-streaming" : ""}`}>
      <AssistantFrom at={message.at}>
        {done && (body.trim() || visuals.length || sql) ? <ExportMenu message={message} question={question} /> : null}
      </AssistantFrom>
      <div className="mizu-answer">
        {investigation ? (
          <div className="mizu-answer-rca">
            <SearchIcon size={13} />
            <span className="mizu-answer-rca-label">Root cause analysis</span>
            {investigation.title ? <span className="mizu-answer-rca-title">{investigation.title}</span> : null}
          </div>
        ) : null}
        {sql?.kind === "result" ? (
          <div className="mizu-answer-rca is-super">
            <DatabaseIcon size={13} />
            <span className="mizu-answer-rca-label">Query result</span>
            {sql.title ? <span className="mizu-answer-rca-title">{sql.title}</span> : null}
          </div>
        ) : null}
        {streaming && !body.trim() ? <Progress status={message.status} investigating={message.investigating} superMode={message.superMode} /> : null}
        <ChatMarkdown text={body} hideStats={tiled} afterLead={visuals.length ? <Visuals visuals={visuals} onReady={onVisuals} /> : null} />
        {done && sql ? <SqlCard sql={sql} canRun={canSuper} busy={busy} onRun={onRunQuery} /> : null}
        {done && message.navigation ? (
          <PageCard navigation={message.navigation} opened={message.opened} note={message.navNote} onOpen={onOpenPage} />
        ) : null}
        {done && actions.length ? (
          <div className="mizu-answer-actions">
            {actions.map((action) => {
              // A deep link names the context it opens its page with.
              const context = action.kind === "link" ? linkContext(action.navigation) : "";
              return (
                <button
                  key={action.key || action.kind}
                  type="button"
                  className={`mizu-answer-action${action.kind === "locate" ? " primary" : action.kind === "investigate" ? " investigate" : ""}`}
                  title={actionTitle(action)}
                  onClick={() =>
                    action.kind === "investigate"
                      ? onAsk(action.question, "investigate")
                      : action.kind === "link"
                        ? onOpenPage(action.navigation)
                        : onAction(action)
                  }
                >
                  {action.kind === "locate" ? <PinIcon size={15} /> : action.kind === "investigate" ? <SearchIcon size={15} /> : null}
                  <span className="mizu-answer-action-label">{action.label}</span>
                  {context ? <span className="mizu-answer-action-context">{context}</span> : null}
                  {action.kind === "locate" || action.kind === "investigate" ? null : <ArrowIcon size={15} />}
                </button>
              );
            })}
          </div>
        ) : null}
        {done && source ? (
          <div className="mizu-answer-source">
            <InfoIcon size={14} />
            <span>Source: {source}</span>
          </div>
        ) : null}
        {done && latest && followUps.length ? (
          <div className="mizu-answer-follow" role="group" aria-label="Follow-up questions">
            <div className="mizu-answer-follow-label">Follow up</div>
            <div className="mizu-answer-chips">
              {followUps.map((question) => (
                <button
                  key={question}
                  type="button"
                  className={`mizu-chip${followMode(question) === "super" ? " is-super" : ""}`}
                  onClick={() => onAsk(question, followMode(question))}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// The user's conversations, newest first - one opened with a click.
function ConversationHistory({ list, currentId, opening, onOpen, onRetry, onClose }) {
  const { status, items } = list;
  return (
    <div className="mizu-chat-history" id="mizu-chat-history" role="region" aria-label="Previous conversations">
      <div className="mizu-chat-history-head">
        <span>Previous conversations</span>
        <button
          type="button"
          className="mizu-chat-history-close"
          onClick={onClose}
          title="Back to the conversation"
          aria-label="Back to the conversation"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      {status === "loading" && !items.length ? <div className="mizu-chat-history-note">Loading conversations…</div> : null}
      {status === "failed" ? (
        <div className="mizu-chat-history-note">
          {items.length ? "Couldn't refresh your conversations." : "Couldn't load your conversations."}{" "}
          <button type="button" className="mizu-chat-history-retry" onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : null}
      {status === "ready" && !items.length ? (
        <div className="mizu-chat-history-note">No previous conversations yet. The questions you ask are kept here.</div>
      ) : null}
      {items.length ? (
        <ul className="mizu-chat-history-list">
          {items.map((item) => {
            const current = item.id === currentId;
            const busy = opening?.id === item.id;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`mizu-chat-history-item${current ? " current" : ""}`}
                  aria-current={current ? "true" : undefined}
                  onClick={() => onOpen(item)}
                >
                  <span className="mizu-chat-history-title">{item.title}</span>
                  <span className={`mizu-chat-history-meta${busy && opening.failed ? " failed" : ""}`}>
                    {busy
                      ? opening.failed
                        ? "Couldn't open it - click to try again"
                        : "Opening…"
                      : [whenOf(item.updated_at), `${item.message_count} messages`, current ? "Current" : null]
                          .filter(Boolean)
                          .join(" · ")}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

const ChatbotWidget = ({ open, onOpenChange, showButton = true }) => {
  const [openState, setOpenState] = useState(false);
  const isOpen = open ?? openState;
  const setIsOpen = (next) => {
    if (open === undefined) setOpenState(next);
    onOpenChange?.(next);
  };
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Its size, spot and pinning (remembered), full screen (not), and the
  // browser window they are held to.
  const [layout, setLayout] = useState(loadLayout);
  const [fullScreen, setFullScreen] = useState(false);
  const [view, setView] = useState(viewportSize);
  // A drag or resize under way - "move", or the edge pulled - once the press
  // has travelled; the press itself, from the moment it lands.
  const [gesture, setGesture] = useState(null);
  const press = useRef(null);
  // The conversation shown: its questions and answers ({ role, content, at,
  // and an answer's tools, meterIds, investigation and visuals - and, on its
  // way, its id, streaming and status }), and its id on the server and
  // title. The id is null for a new conversation (its first question starts
  // it), and undefined until the most recent one has loaded - a question
  // then goes to the most recent.
  const [messages, setMessages] = useState([]);
  // The answer on its way: its message's id, its text not yet drawn (drawn
  // once a frame), and its request - let go when another conversation is
  // shown (the server still finishes it, in its own conversation).
  const pending = useRef(null);
  const [conversation, setConversation] = useState({ id: undefined, title: null });
  // The most recent conversation loading, as the window first opens.
  const [restoring, setRestoring] = useState(true);
  const restoreWanted = useRef(true);
  // Bumped when another conversation is shown (New chat, or one from
  // History): an answer still coming for the one before is not added to it.
  const shown = useRef(0);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Investigation mode: the questions typed or picked are answered with a
  // root cause analysis. Off each time the page loads.
  const [investigating, setInvestigating] = useState(false);
  // Super Mode: they are answered with a SQL query to approve - offered
  // when the API says it is open to the user (its "features"); off each
  // time the page loads, and never on with Investigation mode.
  const [superAvailable, setSuperAvailable] = useState(false);
  const [superOn, setSuperOn] = useState(false);
  const superMode = superOn && superAvailable;
  // Page context: what the user is looking at - the page's name and the
  // district, with what the page publishes (pageContext.js) - sent with each
  // question while it is on (remembered); its details open by the message
  // box.
  const [contextOn, setContextOn] = useState(loadContextOn);
  const [contextOpen, setContextOpen] = useState(false);
  const { selectedDistrict, setSelectedDistrict } = useDistrict() || {};
  const pageParts = usePageParts();
  const pageTitle = PAGE_TITLES[pathname.toLowerCase()] || null;
  const pageContext = useMemo(
    () => mergePageContext({ page: pageTitle, district: districtLabel(selectedDistrict) }, pageParts),
    [pageTitle, selectedDistrict, pageParts],
  );
  const sentContext = contextOn ? pageContext : null;
  // History: the list of conversations, and the one being opened from it.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState({ status: "idle", items: [] });
  const [opening, setOpening] = useState(null);
  const openRequest = useRef(0);
  const asked = messages.some((msg) => msg.role === "user");
  const firstQuestion = messages.find((msg) => msg.role === "user")?.content;
  const conversationTitle = conversation.title || firstQuestion || (restoring ? "" : "New conversation");

  const bodyRef = useRef(null);
  // The conversation's end in view: kept there as an answer grows, unless
  // the reader scrolls back up.
  const pinned = useRef(true);
  const inputRef = useRef(null);

  // The floating button, where it was dragged to.
  const [position, setPosition] = useState({ right: 24, bottom: 24 });
  const isDragging = useRef(false);
  const buttonPress = useRef(null);

  const pinnable = canPin(view);
  const docked = layout.docked && pinnable;
  const dockWidth = Math.round(clamp(layout.dockWidth, DOCK.min, maxDockWidth(view)));
  const rect = fitRect(layout.rect || besideButton(position, view), view);
  const mode = fullScreen ? "fullscreen" : docked ? "docked" : "floating";

  useEffect(() => {
    const onResize = () =>
      setView((current) => {
        const next = viewportSize();
        return current.width === next.width && current.height === next.height ? current : next;
      });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Remembered when a drag or resize ends, not at every step of one.
  useEffect(() => {
    if (!gesture) saveLayout(layout);
  }, [layout, gesture]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PAGE_CONTEXT_KEY, contextOn ? "on" : "off");
    } catch {
      // Storage blocked: kept for this visit.
    }
  }, [contextOn]);

  // Pinned and open: the app makes room for it (before the panel is drawn
  // over it).
  const dockPx = isOpen && docked ? dockWidth : 0;
  useLayoutEffect(() => {
    if (!dockPx) return undefined;
    const html = document.documentElement;
    html.classList.add("mizu-chat-docked");
    html.style.setProperty("--mizu-chat-dock", `${dockPx}px`);
    announceResize();
    return () => {
      html.classList.remove("mizu-chat-docked");
      html.style.removeProperty("--mizu-chat-dock");
      announceResize();
    };
  }, [dockPx]);

  // Full screen: the page under it does not scroll.
  const covering = isOpen && fullScreen;
  useLayoutEffect(() => {
    if (!covering) return undefined;
    const html = document.documentElement;
    html.classList.add("mizu-chat-fullscreen");
    return () => html.classList.remove("mizu-chat-fullscreen");
  }, [covering]);

  // While dragging or resizing: its cursor everywhere, no text selected on
  // the way, and frames (the dashboard's original-build tabs) not taking the
  // pointer from it.
  useLayoutEffect(() => {
    if (!gesture) return undefined;
    const html = document.documentElement;
    html.classList.add("mizu-chat-busy");
    html.style.setProperty("--mizu-chat-cursor", CURSORS[gesture]);
    return () => {
      html.classList.remove("mizu-chat-busy");
      html.style.removeProperty("--mizu-chat-cursor");
    };
  }, [gesture]);

  useEffect(() => {
    if (!covering) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setFullScreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [covering]);

  // A press on the title bar, footer or grip ("move"), or on an edge or
  // corner: a drag or resize once it travels DRAG_THRESHOLD - short of that,
  // a click. The pressed element keeps the pointer (capture) until it lifts.
  const startGesture = (event, kind) => {
    if (fullScreen || (event.pointerType === "mouse" && event.button !== 0)) return;
    if (kind === "move" && event.target.closest("button, input, textarea, form, a, .mizu-chat-context-panel")) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    press.current = {
      kind,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      rect,
      docked,
      dockWidth,
      moved: false,
    };
  };

  const moveGesture = (event) => {
    const g = press.current;
    if (!g || event.pointerId !== g.pointerId) return;
    const dx = event.clientX - g.x;
    const dy = event.clientY - g.y;
    if (!g.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      g.moved = true;
      setGesture(g.kind);
      if (g.kind === "move" && g.docked) {
        // Pulled off the right edge: it floats again at its last size, held
        // where it was across its title bar.
        const size = layout.rect ? fitRect(layout.rect, view) : SIZE;
        const across = clamp((g.x - (view.width - g.dockWidth)) / g.dockWidth, 0, 1);
        g.rect = { left: g.x - across * size.width, top: 0, width: size.width, height: size.height };
        g.docked = false;
        setLayout((current) => ({ ...current, docked: false }));
      }
    }
    if (g.kind === "move") {
      const next = fitRect({ ...g.rect, left: g.rect.left + dx, top: g.rect.top + dy }, view);
      setLayout((current) => ({ ...current, rect: next }));
    } else if (g.docked) {
      const width = clamp(g.dockWidth - dx, DOCK.min, maxDockWidth(view));
      setLayout((current) => ({ ...current, dockWidth: width }));
    } else {
      const next = resizeRect(g.rect, g.kind, dx, dy, view);
      setLayout((current) => ({ ...current, rect: next }));
    }
  };

  const endGesture = (event) => {
    const g = press.current;
    if (!g || event.pointerId !== g.pointerId) return;
    press.current = null;
    setGesture(null);
  };

  const gestureHandlers = (kind) => ({
    onPointerDown: (event) => startGesture(event, kind),
    onPointerMove: moveGesture,
    onPointerUp: endGesture,
    onPointerCancel: endGesture,
    // The pointer taken away some other way (the browser losing focus).
    onLostPointerCapture: endGesture,
  });

  // The floating button: dragged anywhere, or clicked.
  const handleBtnPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    isDragging.current = false;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    buttonPress.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, from: position };
  };

  const handleBtnPointerMove = (event) => {
    const b = buttonPress.current;
    if (!b || event.pointerId !== b.pointerId) return;
    const deltaX = event.clientX - b.x;
    const deltaY = event.clientY - b.y;
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      isDragging.current = true;
    }
    if (!isDragging.current) return;
    setPosition({
      right: Math.max(10, Math.min(window.innerWidth - 70, b.from.right - deltaX)),
      bottom: Math.max(10, Math.min(window.innerHeight - 70, b.from.bottom - deltaY))
    });
  };

  const handleBtnPointerUp = (event) => {
    if (buttonPress.current?.pointerId === event.pointerId) buttonPress.current = null;
  };

  const togglePinned = () => {
    setFullScreen(false);
    setLayout((current) => ({ ...current, docked: !docked }));
  };

  const toggleFullScreen = () => setFullScreen((on) => !on);

  const handleBtnClick = (e) => {
    if (isDragging.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    handleToggle();
  };

  // The latest message in view, as it grows - not before anything is asked
  // (the welcome stays at the top), nor while History is shown, nor once
  // the reader has scrolled up to read.
  const keepPinned = useCallback(() => {
    const body = bodyRef.current;
    if (body && pinned.current) body.scrollTop = body.scrollHeight;
  }, []);
  const onBodyScroll = () => {
    const body = bodyRef.current;
    if (body) pinned.current = body.scrollHeight - body.scrollTop - body.clientHeight < 80;
  };
  useLayoutEffect(() => {
    if (isOpen && asked && !historyOpen) keepPinned();
  }, [messages, isOpen, asked, historyOpen, keepPinned]);

  // Closed for good (signed out): an answer on its way is let go.
  useEffect(() => () => pending.current?.controller.abort(), []);

  // The most recent conversation, as the window first opens - unless a
  // question, New chat or History got there first.
  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      if (!signedIn()) return;
      try {
        const data = await chatbotApi("get");
        if (cancelled) return;
        setSuperAvailable(data.features?.super_mode === true);
        if (!restoreWanted.current) return;
        const restored = toMessages(data.messages);
        setMessages(restored);
        // None yet: the next question starts one.
        setConversation({ id: data.conversation_id ?? (restored.length ? undefined : null), title: data.title || null });
      } catch (err) {
        console.error("Failed to load chat history:", err);
      }
    };
    restore().finally(() => {
      if (!cancelled) setRestoring(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Another conversation shown: a new one, or one opened from History.
  const show = (next, nextMessages) => {
    pending.current?.controller.abort();
    pending.current = null;
    shown.current += 1;
    openRequest.current += 1;
    pinned.current = true;
    restoreWanted.current = false;
    setRestoring(false);
    setConversation(next);
    setMessages(nextMessages);
    setIsLoading(false);
    setOpening(null);
    setHistoryOpen(false);
    inputRef.current?.focus();
  };

  const newChat = () => show({ id: null, title: null }, []);

  const loadHistory = async () => {
    setHistoryList((list) => ({ status: "loading", items: list.items }));
    try {
      const data = await chatbotApi("get", "/conversations");
      setHistoryList({ status: "ready", items: Array.isArray(data.conversations) ? data.conversations : [] });
    } catch (err) {
      console.error("Failed to load conversations:", err);
      setHistoryList((list) => ({ status: "failed", items: list.items }));
    }
  };

  const toggleHistory = () => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    setOpening(null);
    setHistoryOpen(true);
    loadHistory();
  };

  const openConversation = async (item) => {
    if (item.id === conversation.id) {
      setHistoryOpen(false);
      return;
    }
    const request = (openRequest.current += 1);
    setOpening({ id: item.id, failed: false });
    try {
      const data = await chatbotApi("get", "", { params: { conversation_id: item.id } });
      if (request !== openRequest.current) return;
      if (data.features) setSuperAvailable(data.features.super_mode === true);
      show({ id: data.conversation_id || item.id, title: data.title || item.title || null }, toMessages(data.messages));
    } catch (err) {
      if (request !== openRequest.current) return;
      console.error("Failed to open the conversation:", err);
      setOpening({ id: item.id, failed: true });
    }
  };

  const handleToggle = () => {
    if (isOpen) {
      setFullScreen(false);
    }
    setIsOpen(!isOpen);
  };

  // An answer's button: the New Dashboard asked to show the answer's meters
  // on its map or its Alert Center - opened first when another page is
  // showing. Out of full screen, which would hide it. (Its "View on" links
  // open their pages as openPage does.)
  const runAction = (action) => {
    setFullScreen(false);
    requestDashboard(
      action.kind === "locate"
        ? { kind: "locate", meterIds: action.meterIds, alertType: action.alertType, label: action.setLabel, source: action.source }
        : { kind: "tab", tab: action.dashboardTab },
    );
    if (pathname.toLowerCase() !== "/new-dashboard") navigate("/new-dashboard");
  };

  // A page an answer opens, or its card's Open (chatNavigation.js): the page
  // with the context it was asked with, the New Dashboard's tab or its map,
  // or the page before - out of full screen. A district asked for on the
  // dashboard is switched to, as its header switches it, when this user has
  // it (a page's own URL carries it to its context bar, which does the
  // same). Returns what could not be done as asked, or null. Back is to the
  // page on screen now - the answer may arrive after the user moved on - so
  // the location is read here, not from this render.
  const openPage = (navigation) => {
    const target = navigationTarget(navigation, pageAt(window.location.pathname, window.location.search));
    if (!target) return "That page can't be opened from here.";
    setFullScreen(false);
    if (target.back) {
      // Only within the app: never back out of it.
      if (!(window.history.state?.idx > 0)) return "There's no page before this one to go back to.";
      navigate(-1);
      return null;
    }
    let note = null;
    if (target.district && target.district !== selectedDistrict) {
      const mine = Array.isArray(storedData().district) ? storedData().district.map((item) => item?.district_name) : [];
      if (mine.includes(target.district)) {
        try {
          window.localStorage.setItem("MCData", JSON.stringify({ ...storedData(), selectedDistrict: target.district }));
        } catch {
          // Storage blocked: switched for this visit.
        }
        setSelectedDistrict?.(target.district);
      } else {
        note = `District ${target.district} isn't one of yours, so this shows ${selectedDistrict || "your district"}.`;
      }
    }
    if (target.dashboard) {
      requestDashboard(target.dashboard);
      if (pathname.toLowerCase() !== "/new-dashboard") navigate("/new-dashboard");
      return note;
    }
    navigate(target.to, target.state ? { state: target.state } : undefined);
    return note;
  };

  // A question typed in the message box.
  const handleSend = (e) => {
    e.preventDefault();
    if (isLoading || !inputValue.trim()) return;
    setInputValue("");
    ask(inputValue);
  };

  // What the message box suggests as "/" is typed (chatNavigation.js): the
  // commands, then after /open the pages - picked with the arrow keys and
  // Enter or Tab, or a click; Esc hides them until the text changes. A
  // command or page is asked at once; "/open " is put in the box, for the
  // page to follow.
  const suggestions = useMemo(() => commandSuggestions(inputValue), [inputValue]);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [suggestionsHidden, setSuggestionsHidden] = useState(false);
  const suggesting = suggestions.length > 0 && !suggestionsHidden && !isLoading;
  const typeMessage = (value) => {
    setInputValue(value);
    setSuggestionIndex(0);
    setSuggestionsHidden(false);
  };
  const chooseSuggestion = (item) => {
    if (item.send) {
      typeMessage("");
      ask(item.send);
    } else if (item.insert) {
      typeMessage(item.insert);
    }
    inputRef.current?.focus();
  };
  const onInputKey = (event) => {
    if (!suggesting) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setSuggestionIndex((index) => (index + step + suggestions.length) % suggestions.length);
    } else if (event.key === "Tab" || (event.key === "Enter" && !event.shiftKey)) {
      event.preventDefault();
      chooseSuggestion(suggestions[Math.min(suggestionIndex, suggestions.length - 1)]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      // Not full screen's Esc too.
      event.stopPropagation();
      setSuggestionsHidden(true);
    }
  };

  // A question asked - typed, one of the samples or a follow-up - in the
  // conversation shown: as an investigation ("investigate"), in Super Mode
  // ("super") or not ("ask"), by the switches when the caller does not say,
  // and with the page in view while page context is on. A Super Mode query
  // the user approves is run by its id (runQuery), asked as "Run the query:
  // <its title>". Its answer is shown as it comes (chatStream.js): what it
  // is doing, its figures, charts and tables as soon as its data is in, its
  // text as it is written.
  const ask = async (question, mode, { runQuery } = {}) => {
    // One question at a time: the next can be typed while this one is
    // answered, and sent once it has been.
    if (isLoading || !question.trim()) return;
    // Asked before the most recent conversation came in: it stays out.
    restoreWanted.current = false;
    setRestoring(false);
    setHistoryOpen(false);
    const view = shown.current;
    const chosen = mode || (superMode ? "super" : investigating ? "investigate" : "ask");
    const investigate = chosen === "investigate";
    const sqlMode = chosen === "super";
    // Running a query is no question about the page.
    const context = runQuery ? undefined : sentContext || undefined;
    const live = { id: `answer-${Date.now()}`, text: "", frame: 0, controller: new AbortController() };
    // The district its data is read for (the request's District header).
    const askedDistrict = storedData().selectedDistrict || null;
    pending.current = live;
    pinned.current = true;
    setContextOpen(false);

    setMessages((prev) => [
      ...prev,
      { role: "user", content: question, at: new Date().toISOString(), mode: modeOf(chosen), runQuery, context },
      {
        role: "assistant",
        id: live.id,
        content: "",
        at: null,
        streaming: true,
        status: { stage: "thinking" },
        investigating: investigate,
        superMode: sqlMode,
        tools: [],
        meterIds: [],
        investigation: null,
        visuals: [],
        sql: null,
      },
    ]);
    setIsLoading(true);
    // The message box keeps the focus - asked with Send or a sample too,
    // whose press took it - so the next question is typed straight in.
    inputRef.current?.focus();

    // The answer's message, changed - while this conversation is shown.
    const patch = (change) => {
      if (view !== shown.current) return;
      setMessages((prev) => prev.map((msg) => (msg.id === live.id ? { ...msg, ...(typeof change === "function" ? change(msg) : change) } : msg)));
    };
    const draw = () => {
      live.frame = 0;
      patch({ content: live.text });
    };
    const onEvent = (name, data) => {
      if (name === "status") patch({ status: data });
      else if (name === "visual") patch((msg) => ({ visuals: [...msg.visuals, data] }));
      else if (name === "delta") {
        live.text += data?.text || "";
        if (!live.frame) live.frame = window.requestAnimationFrame(draw);
      } else if (name === "reset") {
        live.text = "";
        patch({ content: "" });
      }
    };

    try {
      const body = { message: question };
      if (conversation.id !== undefined) body.conversation_id = conversation.id;
      if (investigate || sqlMode) body.mode = chosen;
      if (runQuery) body.run_query = runQuery;
      if (context) body.context = context;
      const data = await streamChat({ ...chatbotEndpoint(), body, signal: live.controller.signal, onEvent });
      // Another conversation shown since: this answer is kept in its own.
      if (view !== shown.current) return;
      if (data.conversation_id) setConversation({ id: data.conversation_id, title: data.title || null });
      const sql = sqlOf(data.sql);
      const navigation = navigationOf(data.navigation);
      // The page it opens, opened now - one to pick from waits for the pick.
      const opening = navigation && (navigation.page || navigation.back);
      const navNote = opening ? openPage(navigation) : null;
      patch({
        content: data.response ?? live.text,
        at: new Date().toISOString(),
        streaming: false,
        status: null,
        tools: Array.isArray(data.tools_used) ? data.tools_used : [],
        meterIds: Array.isArray(data.meter_ids) ? data.meter_ids : [],
        investigation: investigationOf(data.investigation),
        visuals: visualsOf(data.visuals),
        sql,
        navigation,
        opened: !!opening,
        navNote,
        district: typeof data.district === "string" && data.district ? data.district : askedDistrict,
      });
      // The query approved: marked as run - or stopped - where it was proposed.
      if (runQuery && sql?.kind === "result") {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.sql?.kind === "proposal" && msg.sql.id === runQuery
              ? { ...msg, sql: { ...msg.sql, status: sql.status === "ran" ? "ran" : "failed", ran_at: sql.ran_at, row_count: sql.row_count } }
              : msg,
          ),
        );
      }
    } catch (error) {
      if (view !== shown.current || error?.name === "AbortError") return;
      console.error("Chatbot request error:", error);
      // Refused (403), or not checked (503): why, as the API says it - Super
      // Mode closed to the account, a district it cannot read.
      const content =
        error?.status === 404
          ? "This conversation is no longer available. Start a new chat to keep going."
          : (error?.status === 403 || error?.status === 503) && error.message
            ? error.message
            : "Sorry, I encountered an error processing your request. Please try again.";
      patch({ content, at: new Date().toISOString(), streaming: false, status: null, visuals: [] });
    } finally {
      window.cancelAnimationFrame(live.frame);
      if (pending.current === live) pending.current = null;
      if (view === shown.current) setIsLoading(false);
    }
  };

  // A Super Mode query's Run: the user's approval, asked by its id.
  const runQuery = (sql) => ask(`Run the query: ${sql.title || "SQL query"}`, "super", { runQuery: sql.id });

  // The Investigate and Super switches: one mode at a time.
  const toggleInvestigating = () => {
    setInvestigating((on) => !on);
    setSuperOn(false);
    inputRef.current?.focus();
  };
  const toggleSuper = () => {
    setSuperOn((on) => !on);
    setInvestigating(false);
    inputRef.current?.focus();
  };

  // Where the window is: its own box floating, the right edge pinned, the
  // whole browser window in full screen.
  const getContainerStyle = () => {
    if (mode === "fullscreen") {
      return { top: 0, right: 0, bottom: 0, left: 0, width: "auto", height: "auto" };
    }
    if (mode === "docked") {
      return { top: 0, right: 0, bottom: 0, left: "auto", width: `${dockWidth}px`, height: "auto" };
    }
    return {
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      right: "auto",
      bottom: "auto",
      width: `${rect.width}px`,
      height: `${rect.height}px`
    };
  };

  return (
    <>
      {/* Scope CSS inside a tag to prevent collision */}
      <style>{`
        .mizu-chat-btn {
          position: fixed;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          /* MizuIQ's avatar, as on the 2.0 screens: navy, ringed in the assistant's purple. */
          background: #0e1a2c;
          box-shadow: 0 0 0 4px rgba(183, 148, 255, 0.12), 0 10px 24px rgba(0, 0, 0, 0.45);
          border: 2px solid #b794ff;
          cursor: grab;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          touch-action: none;
        }
        .mizu-chat-btn:active {
          cursor: grabbing;
        }
        .mizu-chat-btn:hover {
          transform: scale(1.08);
          box-shadow: 0 0 0 6px rgba(183, 148, 255, 0.18), 0 12px 28px rgba(0, 0, 0, 0.5);
        }
        .mizu-chat-btn svg {
          width: 26px;
          height: 26px;
          color: #b794ff;
          transition: transform 0.3s ease;
        }
        .mizu-chat-btn.open svg {
          transform: rotate(90deg);
        }
        /* The design's colours: a dark navy window, teal for what can be
           done, red for what needs attention. */
        .mizu-chat-container {
          --mc-bg: #111a26;
          --mc-head: #16212f;
          --mc-surface: #1c2838;
          --mc-line: #26364a;
          --mc-edge: #31445a;
          --mc-text: #e8eef5;
          --mc-text-2: #adbaca;
          --mc-muted: #8493a5;
          --mc-accent: #8edde8;
          --mc-accent-fill: #2fa6ba;
          --mc-accent-bg: #172734;
          --mc-accent-bg-2: #1b313f;
          --mc-accent-line: #2c5e6c;
          --mc-chip-line: #27515e;
          --mc-alert: #f07c74;
          --mc-warn: #f2b45a;
          --mc-good: #69c68c;
          /* Super Mode's: MizuIQ's own violet (its button's ring). */
          --mc-super: #b794ff;
          --mc-super-bg: rgba(183, 148, 255, 0.08);
          --mc-super-line: rgba(183, 148, 255, 0.42);
          position: fixed;
          bottom: 96px;
          right: 24px;
          width: 400px;
          height: 600px;
          background: var(--mc-bg);
          border: 1px solid var(--mc-edge);
          border-radius: 16px;
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
          color: var(--mc-text-2);
          font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          container-type: size;
          transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), visibility 0s;
          transform-origin: bottom right;
        }
        .mizu-chat-container * {
          box-sizing: border-box;
        }
        /* Not clipped to its rounded corners (its resize handles reach past
           them): its title bar and grip are rounded instead. */
        .mizu-chat-container.is-floating .mizu-chat-header {
          border-radius: 15px 15px 0 0;
        }
        .mizu-chat-container.is-floating .mizu-chat-drag-handle-bottom {
          border-radius: 0 0 15px 15px;
        }
        .mizu-chat-container.closed {
          opacity: 0;
          transform: scale(0.8) translate(20px, 20px);
          pointer-events: none;
          visibility: hidden;
          transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), visibility 0s linear 0.3s;
        }
        /* Pinned to the right: a full-height panel beside the app, under the
           app's dialogs and drop-downs. */
        .mizu-chat-container.is-docked {
          border-width: 0 0 0 1px;
          border-radius: 0;
          box-shadow: -12px 0 30px rgba(0, 0, 0, 0.35);
          z-index: 45;
          transform-origin: right center;
        }
        .mizu-chat-container.is-docked.closed {
          transform: translateX(24px);
        }
        /* Full screen: the whole browser window, over everything. */
        .mizu-chat-container.is-fullscreen {
          border: 0;
          border-radius: 0;
          box-shadow: none;
          z-index: 10000;
          transform-origin: center;
        }
        .mizu-chat-container.is-fullscreen.closed {
          transform: scale(0.96);
        }
        .mizu-chat-header {
          flex: 0 0 auto;
          height: 56px;
          padding: 0 12px 0 18px;
          background: var(--mc-head);
          border-bottom: 1px solid var(--mc-line);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          cursor: grab;
          user-select: none;
          touch-action: none;
        }
        .mizu-chat-header:active {
          cursor: grabbing;
        }
        .mizu-chat-container.is-fullscreen .mizu-chat-header,
        .mizu-chat-container.is-fullscreen .mizu-chat-footer {
          cursor: default;
        }
        .mizu-chat-title-group {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }
        .mizu-chat-status {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--mc-good);
          box-shadow: 0 0 0 4px rgba(105, 198, 140, 0.16);
          flex: 0 0 auto;
        }
        .mizu-chat-title {
          font-weight: 700;
          color: var(--mc-text);
          font-size: 14px;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .mizu-chat-header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 0 0 auto;
        }
        .mizu-chat-action,
        .mizu-chat-close {
          width: 32px;
          height: 32px;
          padding: 0;
          border: 0;
          border-radius: 8px;
          background: transparent;
          cursor: pointer;
          color: var(--mc-text-2);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s, background 0.2s;
        }
        .mizu-chat-action:hover,
        .mizu-chat-close:hover {
          color: var(--mc-text);
          background: rgba(255, 255, 255, 0.06);
        }
        .mizu-chat-action.active {
          color: var(--mc-accent);
          background: var(--mc-accent-bg);
        }
        .mizu-chat-body {
          flex: 1;
          min-height: 0;
          padding: 18px 16px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 17px;
        }
        /* Floating, its right edge is a resize handle: the scrollbar keeps
           clear of it. */
        .mizu-chat-container.is-floating .mizu-chat-body {
          margin-right: 6px;
          padding-right: 10px;
        }
        /* Full screen: the conversation in a readable column. */
        .mizu-chat-container.is-fullscreen .mizu-chat-body {
          padding-left: max(16px, calc((100% - 960px) / 2));
          padding-right: max(16px, calc((100% - 960px) / 2));
        }
        .mizu-chat-container.is-fullscreen .mizu-chat-form {
          max-width: 928px;
          margin: 0 auto;
        }
        /* Bottom drag handle */
        .mizu-chat-drag-handle-bottom {
          flex: 0 0 auto;
          height: 22px;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-top: 10px;
          cursor: grab;
          background: var(--mc-bg);
          user-select: none;
          touch-action: none;
        }
        .mizu-chat-drag-handle-bottom:active {
          cursor: grabbing;
        }
        .mizu-chat-container.is-docked .mizu-chat-drag-handle-bottom,
        .mizu-chat-container.is-fullscreen .mizu-chat-drag-handle-bottom {
          display: none;
        }
        .mizu-drag-pill {
          width: 36px;
          height: 4px;
          border-radius: 2px;
          background: var(--mc-edge);
          transition: background 0.2s ease, width 0.2s ease;
        }
        .mizu-chat-drag-handle-bottom:hover .mizu-drag-pill {
          background: var(--mc-accent-fill);
          width: 48px;
        }
        /* Resize handles: the edges and corners of a floating window, the
           left edge of a pinned one - each reaching 4px past the edge, so
           it is easy to catch. */
        .mizu-chat-resize {
          position: absolute;
          z-index: 3;
          touch-action: none;
        }
        .mizu-chat-resize--n { top: -4px; left: 12px; right: 12px; height: 8px; cursor: ns-resize; }
        .mizu-chat-resize--s { bottom: -4px; left: 12px; right: 12px; height: 8px; cursor: ns-resize; }
        .mizu-chat-resize--e { right: -4px; top: 12px; bottom: 12px; width: 8px; cursor: ew-resize; }
        .mizu-chat-resize--w { left: -4px; top: 12px; bottom: 12px; width: 8px; cursor: ew-resize; }
        .mizu-chat-resize--ne { top: -4px; right: -4px; width: 16px; height: 16px; cursor: nesw-resize; }
        .mizu-chat-resize--nw { top: -4px; left: -4px; width: 16px; height: 16px; cursor: nwse-resize; }
        .mizu-chat-resize--sw { bottom: -4px; left: -4px; width: 16px; height: 16px; cursor: nesw-resize; }
        .mizu-chat-resize--se { bottom: -4px; right: -4px; width: 18px; height: 18px; cursor: nwse-resize; }
        .mizu-chat-resize--se::after {
          content: "";
          position: absolute;
          right: 7px;
          bottom: 7px;
          width: 7px;
          height: 7px;
          border-right: 2px solid rgba(255, 255, 255, 0.22);
          border-bottom: 2px solid rgba(255, 255, 255, 0.22);
          border-bottom-right-radius: 2px;
        }
        .mizu-chat-resize--se:hover::after {
          border-color: var(--mc-accent-fill);
        }
        .mizu-chat-container.is-docked .mizu-chat-resize--w {
          top: 0;
          bottom: 0;
          width: 9px;
        }
        .mizu-chat-container.is-docked .mizu-chat-resize--w::after {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          left: 3px;
          width: 2px;
          background: transparent;
          transition: background 0.2s ease;
        }
        .mizu-chat-container.is-docked .mizu-chat-resize--w:hover::after,
        .mizu-chat-container.is-busy .mizu-chat-resize--w::after {
          background: var(--mc-accent-fill);
        }

        /* Custom scrollbar */
        .mizu-chat-body::-webkit-scrollbar {
          width: 6px;
        }
        .mizu-chat-body::-webkit-scrollbar-track {
          background: transparent;
        }
        .mizu-chat-body::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.14);
          border-radius: 4px;
        }
        .mizu-chat-body::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.28);
        }
        /* A message: a question, in its bubble on the right with its time
           under it; an answer, from the assistant, across the window. */
        .mizu-msg {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .mizu-msg.user {
          align-items: flex-end;
        }
        .mizu-msg-bubble {
          max-width: 85%;
          padding: 10px 14px;
          border-radius: 12px;
          background: var(--mc-accent-bg-2);
          border: 1px solid var(--mc-accent-line);
          color: var(--mc-text);
          font-size: 13px;
          line-height: 1.4;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
        }
        .mizu-msg-time {
          margin-top: 5px;
          font-size: 10.5px;
          line-height: 1.3;
          color: #818fa2;
        }
        .mizu-msg-mode {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-right: 7px;
          font-weight: 700;
          color: var(--mc-warn);
        }
        .mizu-msg-mode.is-super {
          color: var(--mc-super);
        }
        .mizu-msg-page {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-right: 7px;
          font-weight: 600;
          color: var(--mc-accent);
        }
        .mizu-msg-from {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 11px;
          align-self: flex-start;
        }
        /* With its Export menu: across the answer, the menu at the end. */
        .mizu-msg-from.has-tools {
          align-self: stretch;
        }
        .mizu-export {
          position: relative;
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .mizu-export-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 9px;
          border: 1px solid var(--mc-edge);
          border-radius: 8px;
          background: transparent;
          color: var(--mc-text-2);
          font-family: inherit;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
        }
        .mizu-export-btn:hover:not(:disabled),
        .mizu-export-btn[aria-expanded="true"] {
          border-color: var(--mc-accent-line);
          color: var(--mc-accent);
        }
        .mizu-export-btn:disabled {
          cursor: progress;
          opacity: 0.75;
        }
        .mizu-export-failed {
          font-size: 11px;
          color: var(--mc-warn);
        }
        .mizu-export-menu {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          z-index: 6;
          width: 250px;
          max-width: calc(100vw - 48px);
          max-height: 300px;
          overflow-y: auto;
          padding: 5px;
          border: 1px solid var(--mc-edge);
          border-radius: 12px;
          background: var(--mc-head);
          box-shadow: 0 14px 34px rgba(0, 0, 0, 0.45);
        }
        .mizu-export-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
          width: 100%;
          padding: 7px 10px;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: var(--mc-text);
          font-family: inherit;
          text-align: left;
          cursor: pointer;
        }
        .mizu-export-item:hover,
        .mizu-export-item:focus-visible {
          background: var(--mc-accent-bg-2);
          outline: none;
        }
        .mizu-export-item span {
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 12.5px;
          font-weight: 600;
        }
        .mizu-export-item small {
          font-size: 11px;
          color: var(--mc-muted);
        }
        .mizu-msg-avatar {
          width: 26px;
          height: 26px;
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 7px;
          background: #192f3b;
          border: 1px solid #2f6371;
          color: var(--mc-accent);
        }
        .mizu-msg-avatar svg {
          width: 14px;
          height: 14px;
        }
        .mizu-msg-name {
          font-size: 11.5px;
          font-weight: 700;
          color: var(--mc-text-2);
        }
        .mizu-answer {
          min-width: 0;
          font-size: 13px;
          line-height: 1.58;
          color: var(--mc-text-2);
          overflow-wrap: break-word;
        }
        .mizu-answer-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 11px;
        }
        .mizu-answer-action {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 34px;
          padding: 6px 12px;
          border-radius: 9px;
          border: 1px solid var(--mc-edge);
          background: transparent;
          color: var(--mc-text);
          font-size: 11.5px;
          font-weight: 700;
          line-height: 1.2;
          text-align: left;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .mizu-answer-action:hover {
          border-color: #46607d;
          background: rgba(255, 255, 255, 0.04);
        }
        /* A "View on" link's context: what the page opens with. */
        .mizu-answer-action {
          max-width: 100%;
          min-width: 0;
        }
        /* Its name whole; only the context is cut short. */
        .mizu-answer-action-label {
          flex: 0 0 auto;
          white-space: nowrap;
        }
        .mizu-answer-action-context {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: 500;
          color: var(--mc-text-2);
        }
        .mizu-answer-action-context::before {
          content: "·";
          margin-right: 6px;
        }
        .mizu-answer-action.primary {
          border-color: var(--mc-accent-line);
          background: var(--mc-accent-bg);
          color: var(--mc-accent);
        }
        .mizu-answer-action.primary:hover {
          border-color: #3b7c8c;
          background: var(--mc-accent-bg-2);
        }
        /* Investigation mode's colour: the design's amber. */
        .mizu-answer-action.investigate {
          border-color: rgba(242, 180, 90, 0.45);
          color: var(--mc-warn);
        }
        .mizu-answer-action.investigate:hover {
          border-color: var(--mc-warn);
          background: rgba(242, 180, 90, 0.08);
        }
        /* A root cause analysis's heading: what it is, and what about. */
        .mizu-answer-rca {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 4px 8px;
          margin-bottom: 10px;
          line-height: 1.4;
          color: var(--mc-warn);
        }
        .mizu-answer-rca svg {
          flex: 0 0 auto;
        }
        .mizu-answer-rca-label {
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
        }
        .mizu-answer-rca-title {
          font-size: 11.5px;
          font-weight: 600;
          color: var(--mc-text-2);
        }
        .mizu-answer-rca-title::before {
          content: "·";
          margin-right: 8px;
          color: var(--mc-muted);
        }
        .mizu-answer-rca.is-super {
          color: var(--mc-super);
        }
        /* A Super Mode query (SqlCard): proposed - its title, SQL, checks,
           limits and Run - or, under a result, a line with its SQL on a
           click. */
        .mizu-sql {
          margin: 12px 0 2px;
          overflow: hidden;
          border: 1px solid var(--mc-super-line);
          border-radius: 10px;
          background: var(--mc-super-bg);
        }
        .mizu-sql-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          min-height: 28px;
          padding: 5px 6px 0 12px;
        }
        .mizu-sql-kicker {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: var(--mc-super);
        }
        .mizu-sql-title {
          padding: 2px 12px 9px;
          font-size: 12.5px;
          font-weight: 700;
          line-height: 1.4;
          color: var(--mc-text);
        }
        .mizu-sql-code {
          margin: 0;
          max-height: 240px;
          overflow: auto;
          padding: 10px 12px;
          border-top: 1px solid var(--mc-line);
          border-bottom: 1px solid var(--mc-line);
          background: #0c141e;
          white-space: pre;
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 11.5px;
          line-height: 1.55;
          color: var(--mc-text);
        }
        .mizu-sql-code code {
          font-family: inherit;
        }
        .mizu-sql-k {
          color: #c9b2ff;
          font-weight: 600;
        }
        .mizu-sql-s {
          color: #9fd8a6;
        }
        .mizu-sql-n {
          color: var(--mc-warn);
        }
        .mizu-sql-c {
          color: var(--mc-muted);
          font-style: italic;
        }
        .mizu-sql-checks {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          padding: 9px 12px 0;
          font-size: 11px;
          line-height: 1.45;
          color: var(--mc-good);
        }
        .mizu-sql-checks svg {
          flex: 0 0 auto;
          margin-top: 1px;
        }
        .mizu-sql-checks span {
          min-width: 0;
          color: var(--mc-text-2);
          overflow-wrap: anywhere;
        }
        .mizu-sql-checks.is-refused {
          padding-bottom: 11px;
          color: var(--mc-alert);
        }
        .mizu-sql-foot {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px 12px;
          padding: 10px 12px 12px;
        }
        .mizu-sql-run {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 32px;
          padding: 6px 14px;
          border: 1px solid var(--mc-super);
          border-radius: 9px;
          background: var(--mc-super);
          color: #1b1233;
          font-family: inherit;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.2;
          cursor: pointer;
          transition: filter 0.2s ease, opacity 0.2s ease;
        }
        .mizu-sql-run:hover:not(:disabled) {
          filter: brightness(1.08);
        }
        .mizu-sql-run:disabled {
          cursor: default;
          opacity: 0.45;
        }
        .mizu-sql-state {
          min-width: 0;
          font-size: 11px;
          line-height: 1.4;
          color: var(--mc-muted);
        }
        .mizu-sql.is-rejected {
          border-color: rgba(240, 124, 116, 0.45);
          background: rgba(240, 124, 116, 0.06);
        }
        .mizu-sql.is-rejected .mizu-sql-kicker {
          color: var(--mc-alert);
        }
        .mizu-sql.is-result {
          border-color: var(--mc-line);
          background: transparent;
        }
        .mizu-sql-line {
          display: flex;
          align-items: center;
          gap: 7px;
          min-height: 32px;
          padding: 4px 6px 4px 11px;
          font-size: 11px;
          color: var(--mc-muted);
        }
        .mizu-sql-line svg {
          flex: 0 0 auto;
          color: var(--mc-super);
        }
        .mizu-sql.is-failed .mizu-sql-line svg {
          color: var(--mc-alert);
        }
        .mizu-sql-line-text {
          flex: 1 1 auto;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .mizu-sql-toggle {
          flex: 0 0 auto;
          padding: 3px 8px;
          border: 0;
          border-radius: 5px;
          background: transparent;
          color: var(--mc-text-2);
          font-family: inherit;
          font-size: 10.5px;
          font-weight: 600;
          cursor: pointer;
        }
        .mizu-sql-toggle:hover {
          color: var(--mc-text);
          background: rgba(255, 255, 255, 0.08);
        }
        .mizu-sql-box-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 3px 6px 3px 12px;
          border-top: 1px solid var(--mc-line);
          font-size: 10.5px;
          color: var(--mc-muted);
        }
        .mizu-sql-box-head span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .mizu-sql-box .mizu-sql-code {
          border-bottom: 0;
        }
        /* The page an answer opened (PageCard): its name, the context it was
           opened with, and Open; pages to pick from, as buttons. */
        .mizu-nav {
          display: flex;
          align-items: center;
          gap: 11px;
          margin: 12px 0 2px;
          padding: 10px 10px 10px 12px;
          border: 1px solid var(--mc-accent-line);
          border-radius: 10px;
          background: var(--mc-accent-bg);
        }
        .mizu-nav-icon {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          border-radius: 8px;
          background: #192f3b;
          border: 1px solid #2f6371;
          color: var(--mc-accent);
        }
        .mizu-nav-text {
          flex: 1 1 auto;
          min-width: 0;
        }
        .mizu-nav-label {
          font-size: 12.5px;
          font-weight: 700;
          line-height: 1.35;
          color: var(--mc-text);
        }
        .mizu-nav-details {
          margin-top: 1px;
          font-size: 11px;
          line-height: 1.4;
          color: var(--mc-text-2);
          overflow-wrap: anywhere;
        }
        .mizu-nav-note {
          margin-top: 4px;
          font-size: 11px;
          line-height: 1.4;
          color: var(--mc-warn);
        }
        .mizu-nav-open {
          flex: 0 0 auto;
          min-height: 30px;
          padding: 5px 12px;
          border: 1px solid var(--mc-accent-line);
          border-radius: 8px;
          background: transparent;
          color: var(--mc-accent);
          font-family: inherit;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .mizu-nav-open:hover {
          border-color: #3b7c8c;
          background: var(--mc-accent-bg-2);
        }
        .mizu-nav-choices {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 10px;
        }
        .mizu-nav-choice {
          display: inline-flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 1px;
          min-height: 34px;
          padding: 5px 11px;
          border: 1px solid var(--mc-chip-line);
          border-radius: 9px;
          background: transparent;
          color: var(--mc-accent);
          font-family: inherit;
          font-size: 11.5px;
          font-weight: 700;
          line-height: 1.25;
          text-align: left;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .mizu-nav-choice small {
          font-size: 10px;
          font-weight: 500;
          color: var(--mc-muted);
        }
        .mizu-nav-choice:hover {
          border-color: var(--mc-accent-line);
          background: var(--mc-accent-bg);
        }
        .mizu-answer-action svg {
          flex: 0 0 auto;
        }
        .mizu-answer-source {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-top: 12px;
          font-size: 11px;
          line-height: 1.4;
          color: var(--mc-muted);
        }
        .mizu-answer-source svg {
          flex: 0 0 auto;
        }
        .mizu-answer-follow {
          margin-top: 17px;
        }
        .mizu-answer-follow-label {
          margin-bottom: 7px;
          font-size: 10.5px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          color: #8796a9;
        }
        .mizu-answer-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .mizu-chip {
          min-height: 32px;
          padding: 5px 11px;
          border-radius: 16px;
          border: 1px solid var(--mc-chip-line);
          background: transparent;
          color: var(--mc-accent);
          font-size: 11.5px;
          font-weight: 700;
          line-height: 1.3;
          text-align: left;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .mizu-chip:hover {
          border-color: var(--mc-accent-line);
          background: var(--mc-accent-bg);
        }
        /* Asked in Super Mode: a query refined. */
        .mizu-chip.is-super {
          border-color: var(--mc-super-line);
          color: var(--mc-super);
        }
        .mizu-chip.is-super:hover {
          border-color: var(--mc-super);
          background: var(--mc-super-bg);
        }
        /* The assistant's answers (ChatMarkdown) */
        .mizu-md {
          line-height: 1.58;
        }
        .mizu-md > :first-child {
          margin-top: 0;
        }
        .mizu-md > :last-child {
          margin-bottom: 0;
        }
        .mizu-md p {
          margin: 0 0 10px;
        }
        .mizu-md h3,
        .mizu-md h4,
        .mizu-md h5,
        .mizu-md h6 {
          margin: 16px 0 8px;
          color: var(--mc-text);
          font-weight: 700;
          line-height: 1.3;
        }
        .mizu-md h3 {
          font-size: 14.5px;
        }
        .mizu-md h4 {
          font-size: 14px;
        }
        .mizu-md h5 {
          font-size: 13.5px;
          padding-bottom: 5px;
          border-bottom: 1px solid var(--mc-line);
        }
        .mizu-md h6 {
          font-size: 11px;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: var(--mc-muted);
        }
        .mizu-md strong {
          color: var(--mc-text);
          font-weight: 700;
        }
        .mizu-md ul,
        .mizu-md ol {
          margin: 0 0 10px;
          padding-left: 22px;
        }
        .mizu-md ul {
          list-style: disc;
        }
        .mizu-md ol {
          list-style: decimal;
        }
        .mizu-md ul ul {
          list-style: circle;
        }
        .mizu-md li {
          margin: 3px 0;
        }
        .mizu-md li::marker {
          color: #5fb6c4;
        }
        .mizu-md li > p {
          margin: 0;
        }
        .mizu-md li > ul,
        .mizu-md li > ol {
          margin: 3px 0 4px;
        }
        .mizu-md code {
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 11.5px;
          padding: 1px 5px;
          border-radius: 4px;
          color: var(--mc-text);
          background: var(--mc-surface);
          border: 1px solid var(--mc-line);
        }
        .mizu-md a {
          color: var(--mc-accent);
          text-decoration: underline;
        }
        .mizu-md blockquote {
          margin: 0 0 10px;
          padding: 8px 12px;
          border-left: 3px solid var(--mc-accent-fill);
          border-radius: 0 8px 8px 0;
          background: var(--mc-accent-bg);
          color: var(--mc-text-2);
        }
        .mizu-md hr {
          margin: 14px 0;
          border: 0;
          border-top: 1px solid var(--mc-line);
        }
        /* Headline figures: tiles side by side, two by two where four are
           too many across. */
        .mizu-md-stats {
          --n: 3;
          display: grid;
          grid-template-columns: repeat(var(--n), minmax(0, 1fr));
          gap: 1px;
          margin: 14px 0 12px;
          overflow: hidden;
          border: 1px solid var(--mc-line);
          border-radius: 10px;
          background: var(--mc-line);
        }
        .mizu-md-stats.n-1 { --n: 1; }
        .mizu-md-stats.n-2 { --n: 2; }
        .mizu-md-stats.n-4 { --n: 4; }
        .mizu-md-stats.n-5 > :last-child {
          grid-column: span 2;
        }
        @container (max-width: 520px) {
          .mizu-md-stats.n-4 { --n: 2; }
        }
        .mizu-md-stat {
          min-width: 0;
          padding: 13px 14px 11px;
          background: var(--mc-surface);
        }
        .mizu-md-stat-value {
          font-size: 19px;
          font-weight: 700;
          line-height: 1.15;
          color: var(--mc-text);
          overflow-wrap: anywhere;
        }
        .mizu-md-stat-value.is-long {
          font-size: 14.5px;
          line-height: 1.5;
        }
        .mizu-md-stat-value.is-alert {
          color: var(--mc-alert);
        }
        .mizu-md-stat-value.is-warn {
          color: var(--mc-warn);
        }
        .mizu-md-stat-value.is-good {
          color: var(--mc-good);
        }
        .mizu-md-stat-label {
          margin-top: 2px;
          font-size: 11px;
          line-height: 1.35;
          color: #a4b1c1;
        }
        /* A root cause analysis's likely causes, ranked: each with how much
           of the evidence points at it (bars - not a probability) and the
           evidence itself; the first one marked. */
        .mizu-md .mizu-md-causes {
          list-style: none;
          margin: 6px 0 12px;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 1px;
          overflow: hidden;
          border: 1px solid var(--mc-line);
          border-radius: 10px;
          background: var(--mc-line);
        }
        .mizu-md .mizu-md-cause {
          margin: 0;
          padding: 10px 12px 11px;
          background: var(--mc-surface);
        }
        .mizu-md-cause-head {
          display: flex;
          align-items: flex-start;
          gap: 9px;
        }
        .mizu-md-cause-rank {
          flex: 0 0 auto;
          width: 20px;
          height: 20px;
          margin-top: 1px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          border: 1px solid var(--mc-accent-line);
          background: var(--mc-accent-bg-2);
          color: var(--mc-accent);
          font-size: 10.5px;
          font-weight: 700;
          line-height: 1;
        }
        .mizu-md-cause:first-child .mizu-md-cause-rank {
          border-color: var(--mc-accent-fill);
          background: var(--mc-accent-fill);
          color: #0c141e;
        }
        .mizu-md-cause-name {
          flex: 1 1 auto;
          min-width: 0;
          font-size: 12.5px;
          font-weight: 700;
          line-height: 1.45;
          color: var(--mc-text);
        }
        .mizu-md-cause.is-unlikely .mizu-md-cause-name {
          color: var(--mc-muted);
        }
        .mizu-md-cause-strength {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 2px;
          font-size: 11px;
          font-weight: 700;
          line-height: 1.3;
          white-space: nowrap;
          color: var(--mc-text-2);
        }
        .mizu-md-cause.is-strong .mizu-md-cause-strength {
          color: var(--mc-accent);
        }
        .mizu-md-cause-bars {
          display: inline-flex;
          align-items: flex-end;
          gap: 2px;
          height: 12px;
        }
        .mizu-md-cause-bars i {
          display: block;
          width: 4px;
          border-radius: 1px;
          background: var(--mc-edge);
        }
        .mizu-md-cause-bars i:nth-child(1) { height: 6px; }
        .mizu-md-cause-bars i:nth-child(2) { height: 9px; }
        .mizu-md-cause-bars i:nth-child(3) { height: 12px; }
        .mizu-md-cause-bars i.on {
          background: var(--mc-accent-fill);
        }
        .mizu-md-cause-evidence {
          margin: 4px 0 0 29px;
          font-size: 11.5px;
          line-height: 1.5;
          color: var(--mc-text-2);
        }
        /* An answer's figures, charts and tables (ChatVisuals): after its
           opening sentence, one under another. */
        .mizu-viz-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin: 12px 0 14px;
          min-width: 0;
        }
        .mizu-md > .mizu-viz-list:first-child {
          margin-top: 2px;
        }
        .mizu-viz {
          min-width: 0;
          margin: 0;
        }
        .mizu-viz-kpis .mizu-md-stats {
          margin: 0;
        }
        .mizu-viz-kicker {
          margin-bottom: 6px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: var(--mc-muted);
        }
        .mizu-viz-note {
          margin-top: 3px;
          font-size: 10.5px;
          line-height: 1.35;
          color: var(--mc-muted);
        }
        .mizu-viz-note.is-good {
          color: var(--mc-good);
        }
        .mizu-viz-note.is-alert {
          color: var(--mc-alert);
        }
        .mizu-viz-spark {
          display: block;
          margin-top: 6px;
          max-width: 100%;
        }
        .mizu-viz-chart,
        .mizu-viz-table {
          padding: 10px 12px 8px;
          border: 1px solid var(--mc-line);
          border-radius: 10px;
          background: var(--mc-surface);
        }
        .mizu-viz-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 6px;
        }
        .mizu-viz-titles {
          min-width: 0;
        }
        .mizu-viz-title {
          font-size: 12px;
          font-weight: 700;
          line-height: 1.35;
          color: var(--mc-text);
        }
        .mizu-viz-sub {
          font-size: 11px;
          line-height: 1.4;
          color: var(--mc-muted);
        }
        .mizu-viz-switch {
          flex: 0 0 auto;
          display: inline-flex;
          overflow: hidden;
          border: 1px solid var(--mc-edge);
          border-radius: 8px;
        }
        .mizu-viz-switch button {
          padding: 3px 8px;
          border: 0;
          background: transparent;
          color: var(--mc-text-2);
          font-family: inherit;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
        }
        .mizu-viz-switch button + button {
          border-left: 1px solid var(--mc-edge);
        }
        .mizu-viz-switch button:hover {
          color: var(--mc-text);
        }
        .mizu-viz-switch button.active {
          background: var(--mc-accent-bg-2);
          color: var(--mc-accent);
        }
        .mizu-viz-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 3px 12px;
          margin: 0 0 4px;
          font-size: 11px;
          color: var(--mc-text-2);
        }
        .mizu-viz-legend span {
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }
        .mizu-viz-legend i {
          width: 12px;
          height: 3px;
          border-radius: 2px;
          background: var(--swatch);
        }
        .mizu-viz-legend i.is-dashed {
          background: repeating-linear-gradient(90deg, var(--swatch) 0 4px, transparent 4px 6px);
        }
        .mizu-viz-legend i.is-point {
          width: 7px;
          height: 7px;
          border-radius: 50%;
        }
        .mizu-viz-legend i.is-band {
          width: 12px;
          height: 9px;
          border-radius: 2px;
          background: rgba(105, 198, 140, 0.28);
        }
        .mizu-viz-plot {
          width: 100%;
          min-width: 0;
        }
        .mizu-viz-tip {
          min-width: 140px;
          padding: 7px 9px;
          border: 1px solid var(--mc-edge);
          border-radius: 8px;
          background: #0f1823;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
          font-size: 11.5px;
          color: var(--mc-text);
        }
        .mizu-viz-tip-head {
          margin-bottom: 4px;
          font-weight: 700;
        }
        .mizu-viz-tip-row {
          display: flex;
          align-items: center;
          gap: 6px;
          line-height: 1.6;
          color: var(--mc-text-2);
        }
        .mizu-viz-tip-row i {
          width: 8px;
          height: 8px;
          border-radius: 2px;
        }
        .mizu-viz-tip-row b {
          margin-left: auto;
          padding-left: 10px;
          color: var(--mc-text);
        }
        .mizu-viz-pie {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
          align-items: center;
          gap: 10px;
        }
        @container (max-width: 360px) {
          .mizu-viz-pie {
            grid-template-columns: minmax(0, 1fr);
          }
        }
        .mizu-md .mizu-viz-pie-legend {
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .mizu-md .mizu-viz-pie-legend li {
          display: flex;
          align-items: center;
          gap: 7px;
          margin: 0;
          padding: 2px 0;
          font-size: 11.5px;
          line-height: 1.4;
        }
        .mizu-viz-pie-legend i {
          flex: 0 0 auto;
          width: 9px;
          height: 9px;
          border-radius: 2px;
        }
        .mizu-viz-pie-name {
          min-width: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          color: var(--mc-text-2);
        }
        .mizu-viz-pie-share {
          margin-left: auto;
          padding-left: 8px;
          font-weight: 700;
          color: var(--mc-text);
        }
        .mizu-viz-tool {
          flex: 0 0 auto;
          padding: 3px 9px;
          border: 1px solid var(--mc-edge);
          border-radius: 8px;
          background: transparent;
          color: var(--mc-text-2);
          font-family: inherit;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
        }
        .mizu-viz-tool:hover {
          border-color: var(--mc-accent-line);
          color: var(--mc-accent);
        }
        .mizu-viz-scroll {
          overflow-x: auto;
          margin: 0 -12px;
          border-top: 1px solid var(--mc-line);
        }
        .mizu-viz-scroll.is-all {
          max-height: 320px;
          overflow-y: auto;
        }
        .mizu-viz-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11.5px;
          line-height: 1.45;
        }
        .mizu-viz-table table th,
        .mizu-viz-table table td {
          padding: 6px 12px;
          border-bottom: 1px solid var(--mc-line);
          white-space: nowrap;
        }
        .mizu-viz-table table th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: var(--mc-head);
        }
        .mizu-viz-table table th button {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 0;
          border: 0;
          background: none;
          color: var(--mc-text);
          font-family: inherit;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
        }
        .mizu-viz-sort {
          font-size: 8px;
          opacity: 0;
          color: var(--mc-muted);
        }
        .mizu-viz-table table th button:hover .mizu-viz-sort {
          opacity: 1;
        }
        .mizu-viz-sort.on {
          opacity: 1;
          color: var(--mc-accent);
        }
        .mizu-viz-table table tbody tr:last-child td {
          border-bottom: 0;
        }
        .mizu-viz-table table tbody tr:nth-child(even) {
          background: rgba(255, 255, 255, 0.02);
        }
        .mizu-viz-more {
          margin-top: 6px;
          padding: 2px 0;
          border: 0;
          background: none;
          color: var(--mc-accent);
          font-family: inherit;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
        }
        .mizu-viz-foot {
          margin-top: 4px;
          font-size: 11px;
          color: var(--mc-muted);
        }
        .mizu-viz-skeleton {
          border-radius: 10px;
          border: 1px solid var(--mc-line);
          background: linear-gradient(90deg, var(--mc-surface) 0%, #223044 50%, var(--mc-surface) 100%);
          background-size: 200% 100%;
          animation: mizuShimmer 1.4s linear infinite;
        }
        .mizu-viz-skeleton.is-kpis { height: 74px; }
        .mizu-viz-skeleton.is-chart { height: 262px; }
        .mizu-viz-skeleton.is-table { height: 190px; }
        @keyframes mizuShimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }
        /* An answer on its way: what it is doing until its text comes, then
           its text with a caret where it is being written. */
        .mizu-progress {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 24px;
          margin-bottom: 4px;
        }
        .mizu-progress .mizu-typing-dots {
          flex: 0 0 auto;
        }
        .mizu-msg.is-streaming .mizu-md > p:last-child::after,
        .mizu-msg.is-streaming .mizu-md > :is(ul, ol):last-child > li:last-child::after {
          content: "";
          display: inline-block;
          width: 7px;
          height: 1.05em;
          margin-left: 3px;
          vertical-align: text-bottom;
          border-radius: 1px;
          background: var(--mc-accent);
          animation: mizuCaret 1s steps(1) infinite;
        }
        @keyframes mizuCaret {
          50% { opacity: 0; }
        }
        .mizu-md-table {
          margin: 4px 0 12px;
          max-width: 100%;
          overflow-x: auto;
          border: 1px solid var(--mc-line);
          border-radius: 10px;
        }
        .mizu-md table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11.5px;
          line-height: 1.45;
        }
        .mizu-md th,
        .mizu-md td {
          padding: 7px 10px;
          text-align: left;
          vertical-align: top;
          border-bottom: 1px solid var(--mc-line);
        }
        .mizu-md th {
          font-weight: 700;
          color: var(--mc-text);
          white-space: nowrap;
          background: var(--mc-surface);
        }
        .mizu-md tbody tr:nth-child(even) {
          background: rgba(255, 255, 255, 0.02);
        }
        .mizu-md tbody tr:last-child td {
          border-bottom: 0;
        }
        .mizu-md-code {
          margin: 4px 0 12px;
          overflow: hidden;
          border: 1px solid var(--mc-line);
          border-radius: 10px;
          background: #0c141e;
        }
        .mizu-md-code-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 3px 6px 3px 12px;
          font-size: 10.5px;
          color: var(--mc-muted);
          border-bottom: 1px solid var(--mc-line);
        }
        .mizu-md-copy {
          padding: 2px 8px;
          border: 0;
          border-radius: 4px;
          background: transparent;
          color: var(--mc-text-2);
          font-size: 10.5px;
          cursor: pointer;
        }
        .mizu-md-copy:hover {
          color: var(--mc-text);
          background: rgba(255, 255, 255, 0.08);
        }
        .mizu-md-code pre {
          margin: 0;
          padding: 10px 12px;
          overflow-x: auto;
          white-space: pre;
          font-size: 11.5px;
          line-height: 1.5;
          color: var(--mc-text);
        }
        .mizu-md-code code {
          padding: 0;
          border: 0;
          background: none;
        }
        .mizu-chat-footer {
          position: relative;
          flex: 0 0 auto;
          padding: 12px 14px 0;
          border-top: 1px solid var(--mc-line);
          background: var(--mc-bg);
          cursor: grab;
          touch-action: none;
        }
        /* No grip under it pinned or in full screen. */
        .mizu-chat-container.is-docked .mizu-chat-footer,
        .mizu-chat-container.is-fullscreen .mizu-chat-footer {
          padding-bottom: 14px;
        }
        .mizu-chat-form {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: auto;
        }
        .mizu-chat-input {
          flex: 1;
          min-width: 0;
          height: 44px;
          padding: 0 14px;
          background: var(--mc-surface);
          border: 1px solid var(--mc-edge);
          border-radius: 10px;
          color: var(--mc-text);
          font-family: inherit;
          font-size: 13px;
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .mizu-chat-input::placeholder {
          color: #8796a9;
          opacity: 1;
        }
        .mizu-chat-input:focus {
          border-color: var(--mc-accent-fill);
          box-shadow: 0 0 0 3px rgba(47, 166, 186, 0.15);
        }
        .mizu-chat-send {
          width: 44px;
          height: 44px;
          flex: 0 0 auto;
          border-radius: 10px;
          background: var(--mc-accent-fill);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #0c141e;
          transition: background-color 0.2s ease, opacity 0.2s ease;
        }
        .mizu-chat-send:hover:not(:disabled) {
          background: #3cb9cd;
        }
        /* Nothing to send yet: as it is, in the design; an answer coming:
           dimmed. */
        .mizu-chat-send:disabled {
          cursor: default;
        }
        .mizu-chat-send.is-waiting {
          opacity: 0.45;
        }
        /* In Investigation mode the message box takes its amber. */
        .mizu-chat-input.is-investigating {
          border-color: rgba(242, 180, 90, 0.55);
        }
        .mizu-chat-input.is-investigating:focus {
          border-color: var(--mc-warn);
          box-shadow: 0 0 0 3px rgba(242, 180, 90, 0.15);
        }
        /* And in Super Mode, its violet. */
        .mizu-chat-input.is-super {
          border-color: var(--mc-super-line);
        }
        .mizu-chat-input.is-super:focus {
          border-color: var(--mc-super);
          box-shadow: 0 0 0 3px rgba(183, 148, 255, 0.15);
        }
        /* Under the message box: the Investigate switch, and what it does. */
        .mizu-chat-composer {
          display: flex;
          align-items: center;
          gap: 9px;
          min-height: 28px;
          margin-top: 8px;
          cursor: auto;
        }
        .mizu-chat-container.is-fullscreen .mizu-chat-composer {
          max-width: 928px;
          margin: 8px auto 0;
        }
        .mizu-chat-mode {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 28px;
          padding: 0 11px;
          border-radius: 14px;
          border: 1px solid var(--mc-edge);
          background: transparent;
          color: var(--mc-text-2);
          font-family: inherit;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
        }
        .mizu-chat-mode:hover {
          border-color: #46607d;
          color: var(--mc-text);
        }
        .mizu-chat-mode.active {
          border-color: var(--mc-warn);
          background: rgba(242, 180, 90, 0.12);
          color: var(--mc-warn);
        }
        .mizu-chat-mode.is-super.active {
          border-color: var(--mc-super);
          background: rgba(183, 148, 255, 0.12);
          color: var(--mc-super);
        }
        .mizu-chat-mode-hint {
          min-width: 0;
          font-size: 11px;
          color: var(--mc-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        /* Beside it: the page context the questions are sent with - its
           details above the footer, at the composer's left edge. */
        .mizu-chat-context {
          display: flex;
          min-width: 0;
        }
        .mizu-chat-context-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
          height: 28px;
          padding: 0 11px 0 9px;
          border-radius: 14px;
          border: 1px solid var(--mc-chip-line);
          background: var(--mc-accent-bg);
          color: var(--mc-accent);
          font-family: inherit;
          font-size: 11.5px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
        }
        .mizu-chat-context-chip svg {
          flex: 0 0 auto;
        }
        .mizu-chat-context-chip:hover,
        .mizu-chat-context-chip[aria-expanded="true"] {
          border-color: var(--mc-accent-line);
          background: var(--mc-accent-bg-2);
        }
        .mizu-chat-context-chip.is-off {
          border-color: var(--mc-edge);
          background: transparent;
          color: var(--mc-muted);
        }
        .mizu-chat-context-chip.is-off:hover,
        .mizu-chat-context-chip.is-off[aria-expanded="true"] {
          border-color: #46607d;
          color: var(--mc-text-2);
        }
        .mizu-chat-context-label {
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .mizu-chat-context-panel {
          position: absolute;
          left: 14px;
          bottom: calc(100% + 6px);
          z-index: 6;
          width: min(360px, calc(100% - 28px));
          max-height: 320px;
          overflow-y: auto;
          padding: 12px 14px 13px;
          border: 1px solid var(--mc-edge);
          border-radius: 12px;
          background: var(--mc-head);
          box-shadow: 0 14px 34px rgba(0, 0, 0, 0.45);
          cursor: auto;
        }
        .mizu-chat-container.is-fullscreen .mizu-chat-context-panel {
          left: max(14px, calc(50% - 464px));
        }
        /* The commands and pages suggested as "/" is typed: over the
           conversation, along the message box. */
        .mizu-chat-commands {
          position: absolute;
          left: 14px;
          right: 14px;
          bottom: calc(100% + 6px);
          z-index: 6;
          max-height: 300px;
          overflow-y: auto;
          padding: 5px;
          border: 1px solid var(--mc-edge);
          border-radius: 12px;
          background: var(--mc-head);
          box-shadow: 0 14px 34px rgba(0, 0, 0, 0.45);
          cursor: auto;
        }
        .mizu-chat-container.is-fullscreen .mizu-chat-commands {
          left: max(14px, calc(50% - 464px));
          right: max(14px, calc(50% - 464px));
        }
        .mizu-chat-command {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          width: 100%;
          padding: 8px 10px;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: var(--mc-text);
          font-family: inherit;
          text-align: left;
          cursor: pointer;
        }
        .mizu-chat-command.is-active {
          background: var(--mc-accent-bg-2);
        }
        .mizu-chat-command-label {
          min-width: 0;
          font-size: 12.5px;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .mizu-chat-command-hint {
          flex: 0 0 auto;
          font-size: 11px;
          color: var(--mc-muted);
        }
        .mizu-chat-context-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          font-size: 12px;
          font-weight: 700;
          color: var(--mc-text);
        }
        .mizu-chat-context-note {
          margin: 6px 0 0;
          font-size: 11px;
          line-height: 1.45;
          color: var(--mc-muted);
        }
        .mizu-chat-context-rows {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 5px 12px;
          margin: 10px 0 0;
          padding-top: 10px;
          border-top: 1px solid var(--mc-line);
          font-size: 11.5px;
          line-height: 1.4;
        }
        .mizu-chat-context-rows dt {
          color: var(--mc-muted);
          white-space: nowrap;
        }
        .mizu-chat-context-rows dd {
          margin: 0;
          min-width: 0;
          color: var(--mc-text);
          overflow-wrap: anywhere;
        }
        .mizu-chat-switch {
          position: relative;
          flex: 0 0 auto;
          width: 34px;
          height: 20px;
          padding: 0;
          border: 1px solid var(--mc-edge);
          border-radius: 10px;
          background: var(--mc-surface);
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .mizu-chat-switch.on {
          border-color: var(--mc-accent-fill);
          background: var(--mc-accent-fill);
        }
        .mizu-chat-switch-knob {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #adbaca;
          transition: transform 0.2s ease, background 0.2s ease;
        }
        .mizu-chat-switch.on .mizu-chat-switch-knob {
          transform: translateX(14px);
          background: #0c141e;
        }
        /* Toolbar, under the title bar: the conversation's title, then
           History and New chat */
        .mizu-chat-toolbar {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          height: 44px;
          padding: 0 12px 0 19px;
          background: var(--mc-bg);
          border-bottom: 1px solid var(--mc-line);
        }
        .mizu-chat-toolbar-title {
          flex: 1 1 auto;
          min-width: 0;
          font-size: 11.5px;
          font-weight: 600;
          color: var(--mc-text-2);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .mizu-chat-toolbar-actions {
          display: flex;
          align-items: center;
          gap: 9px;
          flex: 0 0 auto;
        }
        .mizu-chat-tool {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          height: 30px;
          padding: 0 8px;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: var(--mc-text-2);
          font-size: 11.5px;
          font-weight: 600;
          white-space: nowrap;
          cursor: pointer;
          transition: color 0.2s ease, background 0.2s ease;
        }
        .mizu-chat-tool:hover {
          color: var(--mc-text);
          background: rgba(255, 255, 255, 0.05);
        }
        .mizu-chat-tool.active {
          color: var(--mc-accent);
          background: var(--mc-accent-bg);
        }
        /* A new conversation: centred in the window - the assistant, what
           it answers, and sample questions two by two */
        .mizu-chat-welcome {
          margin: auto 0;
          align-self: center;
          width: 100%;
          max-width: 760px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .mizu-chat-welcome-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          color: var(--mc-accent);
          background: #192f3b;
          border: 1px solid #2f6371;
        }
        .mizu-chat-welcome-icon svg {
          width: 22px;
          height: 22px;
        }
        .mizu-chat-welcome.is-investigating .mizu-chat-welcome-icon {
          color: var(--mc-warn);
          background: rgba(242, 180, 90, 0.1);
          border-color: rgba(242, 180, 90, 0.4);
        }
        .mizu-chat-welcome.is-super .mizu-chat-welcome-icon {
          color: var(--mc-super);
          background: rgba(183, 148, 255, 0.1);
          border-color: var(--mc-super-line);
        }
        .mizu-chat-welcome-title {
          font-size: 14.5px;
          font-weight: 700;
          color: var(--mc-text);
        }
        .mizu-chat-welcome-text {
          margin: 6px 0 18px;
          max-width: 560px;
          font-size: 12px;
          line-height: 1.5;
          color: var(--mc-text-2);
        }
        .mizu-chat-welcome-samples {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          width: 100%;
          max-width: 740px;
        }
        @container (max-width: 300px) {
          .mizu-chat-welcome-samples {
            grid-template-columns: 1fr;
          }
        }
        .mizu-chat-sample {
          text-align: left;
          padding: 11px 13px;
          border-radius: 10px;
          border: 1px solid var(--mc-line);
          background: var(--mc-surface);
          color: var(--mc-text);
          font-size: 12px;
          font-weight: 600;
          line-height: 1.4;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
        }
        .mizu-chat-sample:hover:not(:disabled) {
          background: var(--mc-accent-bg);
          border-color: var(--mc-accent-line);
          color: var(--mc-accent);
        }
        .mizu-chat-sample:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .mizu-chat-restoring {
          margin: auto;
          font-size: 12px;
          color: var(--mc-muted);
        }
        /* History: the user's conversations, newest first */
        .mizu-chat-history {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .mizu-chat-history-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: #8796a9;
        }
        .mizu-chat-history-close {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3px;
          border: 0;
          border-radius: 6px;
          background: transparent;
          color: var(--mc-text-2);
          cursor: pointer;
        }
        .mizu-chat-history-close:hover {
          color: var(--mc-text);
          background: rgba(255, 255, 255, 0.06);
        }
        .mizu-chat-history-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .mizu-chat-history-item {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 10px 12px;
          text-align: left;
          border-radius: 10px;
          border: 1px solid var(--mc-line);
          background: var(--mc-surface);
          color: var(--mc-text);
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .mizu-chat-history-item:hover {
          border-color: var(--mc-accent-line);
          background: var(--mc-accent-bg);
        }
        .mizu-chat-history-item.current {
          border-color: var(--mc-accent-line);
          background: var(--mc-accent-bg-2);
        }
        .mizu-chat-history-title {
          font-size: 12.5px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .mizu-chat-history-meta {
          font-size: 11px;
          color: var(--mc-muted);
        }
        .mizu-chat-history-meta.failed {
          color: var(--mc-alert);
        }
        .mizu-chat-history-note {
          padding: 6px 2px;
          font-size: 12px;
          line-height: 1.5;
          color: var(--mc-text-2);
        }
        .mizu-chat-history-retry {
          padding: 0;
          border: 0;
          background: transparent;
          color: var(--mc-accent);
          font-size: 12px;
          text-decoration: underline;
          cursor: pointer;
        }
        /* Loading dots animation - and what the answer is doing */
        .mizu-typing-note {
          font-size: 11.5px;
          line-height: 1.45;
          color: var(--mc-muted);
        }
        .mizu-typing-dots {
          display: flex;
          gap: 5px;
          padding: 6px 0;
          align-items: center;
        }
        .mizu-dot {
          width: 7px;
          height: 7px;
          background: var(--mc-accent);
          border-radius: 50%;
          animation: mizuBlink 1.4s infinite both;
        }
        .mizu-dot:nth-child(2) { animation-delay: .2s; }
        .mizu-dot:nth-child(3) { animation-delay: .4s; }
        @keyframes mizuBlink {
          0% { opacity: .2; }
          20% { opacity: 1; }
          100% { opacity: .2; }
        }

        /* Pinned to the right: the app keeps to the space left of the panel
           (its pages' own fixed parts too - newDashboard.overrides.css), and
           so do react-toastify's toasts. */
        html.mizu-chat-docked #root {
          margin-right: var(--mizu-chat-dock);
        }
        html.mizu-chat-docked {
          --toastify-toast-right: calc(var(--mizu-chat-dock) + var(--toastify-toast-offset, 16px));
        }
        html.mizu-chat-fullscreen {
          overflow: hidden;
        }
        html.mizu-chat-busy,
        html.mizu-chat-busy * {
          cursor: var(--mizu-chat-cursor) !important;
          user-select: none !important;
        }
        html.mizu-chat-busy iframe {
          pointer-events: none !important;
        }
      `}</style>

      {/* Floating Action Button - not over the window pinned or full screen */}
      {showButton && !(isOpen && mode !== "floating") && <button
        type="button"
        className={`mizu-chat-btn ${isOpen ? "open" : ""}`}
        onClick={handleBtnClick}
        onPointerDown={handleBtnPointerDown}
        onPointerMove={handleBtnPointerMove}
        onPointerUp={handleBtnPointerUp}
        onPointerCancel={handleBtnPointerUp}
        onLostPointerCapture={handleBtnPointerUp}
        style={{
          right: `${position.right}px`,
          bottom: `${position.bottom}px`
        }}
        aria-label="Toggle MizuIQ"
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          // MizuIQ's sparkle, as on the 2.0 screens.
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3l1.9 5.6L19.5 10.5l-5.6 1.9L12 18l-1.9-5.6L4.5 10.5l5.6-1.9L12 3z" />
            <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z" />
          </svg>
        )}
      </button>}

      {/* Chat Window Container */}
      <div
        className={`mizu-chat-container is-${mode}${isOpen ? "" : " closed"}${gesture ? " is-busy" : ""}`}
        style={getContainerStyle()}
        role="dialog"
        aria-label="MizuIQ"
        aria-hidden={!isOpen}
      >
        {mode === "fullscreen"
          ? null
          : (mode === "docked" ? ["w"] : RESIZE_EDGES).map((edge) => (
              <div
                key={edge}
                className={`mizu-chat-resize mizu-chat-resize--${edge}`}
                aria-hidden="true"
                {...gestureHandlers(edge)}
              />
            ))}

        {/* Header - dragged by; a double-click toggles full screen */}
        <div
          className="mizu-chat-header"
          {...gestureHandlers("move")}
          onDoubleClick={(e) => {
            if (!e.target.closest("button")) toggleFullScreen();
          }}
        >
          <div className="mizu-chat-title-group">
            <div className="mizu-chat-status"></div>
            <h3 className="mizu-chat-title">MizuIQ</h3>
          </div>
          <div className="mizu-chat-header-actions">
            {pinnable ? (
              <button
                type="button"
                className={`mizu-chat-action mizu-chat-pin${docked ? " active" : ""}`}
                onClick={togglePinned}
                title={docked ? "Unpin from right" : "Pin to right"}
                aria-label={docked ? "Unpin from right" : "Pin to right"}
                aria-pressed={docked}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="16" rx="2"></rect>
                  <line x1="15" y1="4" x2="15" y2="20"></line>
                  {docked ? <path d="M15 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4z" fill="currentColor"></path> : null}
                </svg>
              </button>
            ) : null}
            <button
              type="button"
              className="mizu-chat-action mizu-chat-expand"
              onClick={toggleFullScreen}
              title={fullScreen ? "Exit full screen" : "Full screen"}
              aria-label={fullScreen ? "Exit full screen" : "Full screen"}
              aria-pressed={fullScreen}
            >
              {fullScreen ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20"></polyline>
                  <polyline points="20 10 14 10 14 4"></polyline>
                  <line x1="14" y1="10" x2="21" y2="3"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              )}
            </button>
            <button type="button" className="mizu-chat-close" onClick={handleToggle} title="Close" aria-label="Close MizuIQ">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Toolbar: the conversation's title; History and New chat */}
        <div className="mizu-chat-toolbar">
          <div className="mizu-chat-toolbar-title" title={conversationTitle}>
            {conversationTitle}
          </div>
          <div className="mizu-chat-toolbar-actions">
            <button
              type="button"
              className={`mizu-chat-tool mizu-chat-history-toggle${historyOpen ? " active" : ""}`}
              onClick={toggleHistory}
              title="Previous conversations"
              aria-expanded={historyOpen}
              aria-controls="mizu-chat-history"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 3-6.7L3 8"></path>
                <polyline points="3 3 3 8 8 8"></polyline>
                <polyline points="12 7 12 12 15 15"></polyline>
              </svg>
              History
            </button>
            <button
              type="button"
              className="mizu-chat-tool mizu-chat-new"
              onClick={newChat}
              title="Start a new conversation"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              New chat
            </button>
          </div>
        </div>

        {/* Messages Body */}
        <div className="mizu-chat-body" ref={bodyRef} onScroll={onBodyScroll}>
          {historyOpen ? (
            <ConversationHistory
              list={historyList}
              currentId={conversation.id}
              opening={opening}
              onOpen={openConversation}
              onRetry={loadHistory}
              onClose={() => setHistoryOpen(false)}
            />
          ) : (
            <>
              {/* A question as typed, in its bubble; an answer in full -
                  the follow-up questions under the latest one - or, on
                  its way, as it comes */}
              {messages.map((msg, index) =>
                msg.role === "assistant" ? (
                  <Answer
                    key={msg.id || index}
                    message={msg}
                    question={messages[index - 1]?.role === "user" ? messages[index - 1].content : ""}
                    latest={index === messages.length - 1 && !isLoading}
                    onAsk={ask}
                    onAction={runAction}
                    onVisuals={keepPinned}
                    onRunQuery={runQuery}
                    onOpenPage={openPage}
                    canSuper={superAvailable}
                    busy={isLoading}
                  />
                ) : (
                  <div key={index} className="mizu-msg user">
                    <div className="mizu-msg-bubble">{msg.content}</div>
                    <div className="mizu-msg-time">
                      {msg.mode === "investigate" ? (
                        <span className="mizu-msg-mode">
                          <SearchIcon size={11} />
                          Investigation
                        </span>
                      ) : null}
                      {msg.mode === "super" ? (
                        <span className="mizu-msg-mode is-super">
                          {msg.runQuery ? <PlayIcon size={10} /> : <DatabaseIcon size={11} />}
                          {msg.runQuery ? "Approved" : "Super Mode"}
                        </span>
                      ) : null}
                      {msg.context?.page ? (
                        <span
                          className="mizu-msg-page"
                          title={`Asked on this page:\n${contextRows(msg.context).map(([name, value]) => `${name}: ${value}`).join("\n")}`}
                        >
                          <EyeIcon size={11} />
                          {msg.context.section ? `${msg.context.page} · ${msg.context.section}` : msg.context.page}
                        </span>
                      ) : null}
                      {timeOf(msg.at)}
                    </div>
                  </div>
                ),
              )}

              {/* A new conversation: the welcome and sample questions */}
              {!messages.length && !isLoading ? (
                restoring ? (
                  <div className="mizu-chat-restoring">Loading your conversation…</div>
                ) : (
                  <Welcome onAsk={ask} disabled={isLoading} investigating={investigating} superMode={superMode} context={sentContext} />
                )
              ) : null}

            </>
          )}
        </div>

        {/* Footer Input - dragged by, around the message box */}
        <div className="mizu-chat-footer" {...gestureHandlers("move")}>
          {/* The commands and pages suggested for what is typed after "/" */}
          {suggesting ? (
            <div className="mizu-chat-commands" id="mizu-chat-commands" role="listbox" aria-label="Commands and pages">
              {suggestions.map((item, index) => (
                <button
                  key={item.key}
                  type="button"
                  role="option"
                  aria-selected={index === suggestionIndex}
                  className={`mizu-chat-command${index === suggestionIndex ? " is-active" : ""}`}
                  // The message box keeps the focus.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setSuggestionIndex(index)}
                  onClick={() => chooseSuggestion(item)}
                >
                  <span className="mizu-chat-command-label">{item.label}</span>
                  <span className="mizu-chat-command-hint">{item.hint}</span>
                </button>
              ))}
            </div>
          ) : null}
          <form className="mizu-chat-form" onSubmit={handleSend}>
            {/* Not disabled while an answer comes: a disabled box loses the
                focus, and the next question could not be typed in. */}
            <input
              ref={inputRef}
              type="text"
              className={`mizu-chat-input${superMode ? " is-super" : investigating ? " is-investigating" : ""}`}
              placeholder={
                superMode
                  ? "Ask for data — I'll write the SQL…"
                  : investigating
                    ? "What should I investigate?"
                    : asked
                      ? "Ask a follow-up…"
                      : "Ask a question…"
              }
              aria-label={superMode ? "Your question, to answer with a SQL query" : investigating ? "What to investigate" : "Your question"}
              aria-autocomplete="list"
              aria-controls={suggesting ? "mizu-chat-commands" : undefined}
              aria-expanded={suggesting}
              value={inputValue}
              onChange={(e) => typeMessage(e.target.value)}
              onKeyDown={onInputKey}
            />
            <button
              type="submit"
              className={`mizu-chat-send${isLoading ? " is-waiting" : ""}`}
              disabled={!inputValue.trim() || isLoading}
              title={superMode ? "Write the query" : investigating ? "Investigate" : "Send"}
              aria-label={superMode ? "Write the query" : investigating ? "Investigate" : "Send"}
            >
              <SendIcon size={19} />
            </button>
          </form>
          {/* Investigation mode: on, the questions get a root cause analysis;
              Super Mode (for the accounts it is open to), a SQL query to
              approve; and the page context they are sent with */}
          <div className="mizu-chat-composer">
            <button
              type="button"
              className={`mizu-chat-mode${investigating ? " active" : ""}`}
              aria-pressed={investigating}
              onClick={toggleInvestigating}
              title={
                investigating
                  ? "Investigation mode is on: each question gets a root cause analysis. Click to turn it off."
                  : "Investigation mode: find the likely causes of leaks, anomalies and other issues"
              }
            >
              <SearchIcon size={14} />
              Investigate
            </button>
            {superAvailable ? (
              <button
                type="button"
                className={`mizu-chat-mode is-super${superMode ? " active" : ""}`}
                aria-pressed={superMode}
                onClick={toggleSuper}
                title={
                  superMode
                    ? "Super Mode is on: each question gets a read-only SQL query, which runs when you approve it. Click to turn it off."
                    : "Super Mode: answer any question about your data with a read-only SQL query you approve"
                }
              >
                <DatabaseIcon size={14} />
                Super
              </button>
            ) : null}
            {pageContext ? (
              <PageContextChip
                context={pageContext}
                on={contextOn}
                open={contextOpen}
                onOpenChange={setContextOpen}
                onToggle={() => setContextOn((on) => !on)}
              />
            ) : (
              <span className="mizu-chat-mode-hint">
                {investigating ? "Causes ranked by the evidence" : "Ask why an issue is happening"}
              </span>
            )}
          </div>
        </div>

        {/* Bottom Drag Grip Bar */}
        <div
          className="mizu-chat-drag-handle-bottom"
          {...gestureHandlers("move")}
          title="Drag to reposition window"
        >
          <div className="mizu-drag-pill"></div>
        </div>
      </div>
    </>
  );
};

export default ChatbotWidget;
